'use strict';

import _ from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';
import { Loading, TipButton } from './Control';
import { Forming, Dialog, Confirm, TipBinder } from './Dialogs';
import { HAComponent } from './Generic';
import { Select, Picker, DepPicker, RNumber } from './Input';
import { ColGroup, Freezing } from './Utils';
import { fixList, validate } from '../../../routes/utils/common-util';
import {I18n} from './I18n';

const { getLabel } = OptionUtils;

const _pickRenderData = (data, dataFilter, sift, steppedRows) => {
	if (!data) return data;
	if (dataFilter) data = _.filter(data, dataFilter);
	if (sift) data = sift(data);
	if (steppedRows > 0) data = data.slice(0, steppedRows);

	return [...data];
};

const Manipulators = {
	/**
	 * 泛用型 (不包含 confirm, 因為會被舊的 del 干擾)
	 * <pre>
	 * Signatures:
	 * props.children(checkedData) => JSX.Element
	 * props.onClick(checkedData) => void
	 * </pre>
	 * @param {object} props React Component 參數
	 * @param {JSX.Element|function} props.children 要呈現的按鈕字樣或內容
	 * @param {function} props.onClick
	 * @param {string} [props.btnCss] 按鈕 CSS 的 suffix. (Ex: 'success', 'danger')
	 * @param {number} [props.ico] 按鈕圖示的 fontawesome suffix. (Ex: 'pen', 'trash-alt')
	 * @return {JSX.Element}
	 */
	'default': props => {
		const {
			_self, disabled, data: checkedData, // 來自 Grids 內部
			children, onClick,       // 必填
			btnCss = 'default', ico, // 選填
		} = props;
		return <div {...{
			className: `btn btn-${btnCss}${ico ? ` fa fa-${ico}` : ''}${disabled ? ' disabled' : ''}`,
			...!disabled && _.isFunction(onClick) && { onClick: () => onClick.apply(_self, [checkedData]) },
		}}>
			{_.isFunction(children) ? children(checkedData) : children}
		</div>;
	},
	/**
	 * 刪除用
	 * @param props
	 * @return {JSX.Element}
	 */
	del: props => {
		const { _self, disabled, data: checkedData, exText } = props;
		return <div {...{
			className: `btn btn-danger fa fa-trash-alt ${disabled ? 'disabled' : ''}`,
			...!disabled && {
				onClick: () => {
					const { confirmMsg } = { ..._self.deleteHandler, ...props };
					const msg = (
						_.isString(confirmMsg) ? confirmMsg :
							_.isFunction(confirmMsg) ? confirmMsg(checkedData) : null
					) || `確定要刪除此 ${checkedData.length} 筆資料嗎?`;
					const ids = _.map(checkedData, d => d.id);
					_self.setState({
						confirm: {
							ids, msg,
							yes: () => _.isFunction(_self.deleteHandler) ? _self.deleteHandler(checkedData, _self.state.data) : _self._deleteAjax(ids).send(),
							close: () => _self.setState({ confirm: false }),
						},
					});
				},
			},
		}}>
			刪除<small>({exText}{checkedData.length})</small>
		</div>;
	},
};

class BasicGrid extends HAComponent {
	static version = '0.9.5';
	static propTypes = {
		cols: PropTypes.object,
		ctrlPanel: PropTypes.bool,
		extCtrls: PropTypes.element,
		isBasicCriteria: PropTypes.bool,	// TODO: old version, eliminate it soon!
		isAdvCriteria: PropTypes.bool,		// TODO: old version, eliminate it soon!
		basicCriteria: PropTypes.object,
		advCriteria: PropTypes.object,
		isInfo: PropTypes.bool,
		isRefreshBtn: PropTypes.bool,
		isExportBtn: PropTypes.bool,
		isConfigBtn: PropTypes.bool,
		api: PropTypes.string,
		method: PropTypes.oneOf(['get', 'post']),
		fetchErrMsg: PropTypes.string,
		fetchErrCode: PropTypes.object,
		subApi: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.func]),
		query: PropTypes.object,
		colWidths: PropTypes.array,
		localFilter: PropTypes.func,		// 資料來自 props.data 且使用 props.basicCriteria 時的篩選邏輯
		dataFilter: PropTypes.func,			// like 'sift', but func for lodash's filter! ex: _.filter(src, func);
		sift: PropTypes.func,				// like 'dataFilter', 只剩 _am10130 在用
		newDialog: PropTypes.element,
		editDialog: PropTypes.element,
		fetch: PropTypes.bool,				// 編輯或瀏覽模式時是否重新擷取資料
		browseDialog: PropTypes.element,
		arrangeDialog: PropTypes.element,
		checkable: PropTypes.func,
		checkableMap: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.func])),
		emptyWarning: PropTypes.string,
		onDeleteSuccess: PropTypes.func,
		onLoadSuccess: PropTypes.func,
		gridRow: PropTypes.func, // 自訂每行 tr 裡的內容, signature: (data, idx, this.TD). 沒有的話會用 renderGridBody
		gridTail: PropTypes.func, // 自訂 footer 裡的內容, 需自帶 tr, signature: (data). 沒有的話會用 renderGridFooter
		fetchComponent: PropTypes.func,
		sortFunc: PropTypes.func,
	};
	static defaultProps = {
		caption: false,
		ctrlPanel: true,
		isBasicCriteria: true,		// TODO: old version, eliminate it soon!
		isAdvCriteria: false,		// TODO: old version, eliminate it soon!
		isInfo: true,
		isRefreshBtn: true,
		isExportBtn: false,
		isConfigBtn: false,
		fetchErrMsg: OptionUtils.getLabel('components.grids', 'fetchErrMsg'),
		fetchErrCode: null,
		fetch: true,
		checkable: data => true,
		emptyWarning: OptionUtils.getLabel('components.grids', 'emptyWarning'),
		freezeHeader: true,
		freezing: null, 			// ex: {x: 146, y: 36},
		freezingContainer: null, 	// ex: {width: 500, height: 500},
		gridRow: null,
		gridTail: null,
		rowsPerStepping: 500, // 小於1代表一次全畫，其餘代表每次步進多少筆 (同時也是初始資料的筆數)(僅在 freezing 為 falsy, 且 freezeHeader 為 truly 時有效)
		sortFunc: _.orderBy,
		handleData: data => { return { data };}, // 用來處理 fetch 回來的資料格式
	};
	state = {
		criteriaType: this.props.isBasicCriteria ? 'basic' : this.props.isAdvCriteria ? 'adv' : null,
		_criteria: null,
		_preCriteria: null,
		_preAdvCriteria: null,
		openAdvPanel: false,
		loading: false,
		data: null,
		preCheckedIds: [],
		checkedData: [],
		sortKey: null,
		sortOrder: null,
		error: false,
		newDlg: false,
		editDlg: false,
		browseDlg: false,
		arrDlg: false,
		delIds: null,
		ctrlHint: null,
		advState: null,
		ipe: false,
		confirm: false,
		queryOnEditing: false,
		steppedRows: this.props.rowsPerStepping,
		userPrivileges: {},
		...this.props.initState,
	};
	basicCriteria = this.props.isBasicCriteria ? {} : null;		// TODO: old version, eliminate it soon!
	advCriteria = this.props.isAdvCriteria ? {} : null;			// TODO: old version, eliminate it soon!
	_operatingBehavior = 'default';
	_inputs = {};
	_preCriteria = {};											// TODO: old version, eliminate it soon!
	_criteria = {};												// TODO: old version, eliminate it soon!
	_preAdvCriteria = {};										// TODO: old version, eliminate it soon!

	static _disabledChk = rowSpan => <td className={'-chk'} {...rowSpan ? { rowSpan } : null}><span className={'checkbox disable'} /></td>;
	TH = {
		subIndicator: props => <td {...props.rowSpan ? { rowSpan: props.rowSpan } : null} />,
		checkbox: props => {
			const { rowSpan } = props;
			const { checkedData, data: allData, filteredData, ipe, delIds } = this.state;
			if (ipe || delIds) return BasicGrid._disabledChk(rowSpan);
			const checkableMap = this._getCheckableMap();
			const checkableData = _.filter(filteredData ? filteredData : allData, _data => _.some(checkableMap, (fn) => fn === false ? false : fn === true || fn(_data, this)));
			const checkableCount = checkableData.length;
			if (checkableCount === 0) return BasicGrid._disabledChk(rowSpan);
			const checkedCount = checkedData.length;
			const isCheckedOnClick = checkedCount < checkableCount;
			return <td className={'-chk'} {...rowSpan ? { rowSpan } : null}>
				{checkableCount > 0 && <span {...{
					className: `checkbox ${checkedCount === 0 ? '' : checkedCount === checkableCount ? 'checked' : 'partial'}`,
					onClick: evt => {
						checkableData.forEach(d => d._checked = isCheckedOnClick);
						this.setState({
							preCheckedIds: !isCheckedOnClick ? [] : _.map(checkableData, 'id'),
							checkedData: !isCheckedOnClick ? [] : [...checkableData],
						});
					},
				}} />}
			</td>;
		},
		ctrl: props => {
			const {ctrlHint} = this.state;
			return <td className={'-ctrl'} {...props.rowSpan ? { rowSpan: props.rowSpan } : null}>
				{ctrlHint ? <span className={`msg sm ${ctrlHint.theme ? ctrlHint.theme : 'blue'} text-normal`}>
					{ctrlHint.ico ? <i className={`fa fa-fw fa-${ctrlHint.ico}`} /> : null}
					{ctrlHint.hint}
				</span> : null}
			</td>;
		},
		seq: props => <td className={'-seq'} {...props.rowSpan ? { rowSpan: props.rowSpan } : null}>{props.children === undefined ? '#' : props.children}</td>,
		sortable: props => {
			const { sortKey: key, advState: s, rowSpan } = props;
			const {advState} = this.props;
			const { sortKey: sk, sortOrder: so, criteriaType: t, advState: as, ipe, delIds } = this.state;
			const sortBy = key;
			const sortOrder = (sk == key && so === 'asc') ? 'desc' : 'asc';
			return <td {... {
				... ipe ? null : {className: `-sortable ${sk == key ? so : ''}`},
				...rowSpan ? { rowSpan } : null,
				...!ipe && {
					onClick: delIds ? null : () => {
						const { data } = this.state;
						this.setState({
							sortKey: key,
							sortBy,
							sortOrder,
							...this._sortData(data, sortBy, sortOrder),
						});
			   }}
			}}>{props.children}{s && t === 'adv' ? <span className={'-advState'}>{advState.items[as || advState.init]}</span> : null}</td>;
		},
		i18nSortable: props => {
			const { sortKey: key, rowSpan, showLang=true } = props;
			const { sortKey: sk, sortOrder: so, ipe, delIds } = this.state;
			const sortBy = `${key}.${user.locale}`;
			const sortOrder = (sk == key && so === 'asc') ? 'desc' : 'asc';
			return <td {... {
				... ipe ? null : {className: `-sortable ${sk == key ? so : ''}`},
				...rowSpan ? { rowSpan } : null,
				...!ipe && {
					onClick: delIds ? null : () => {
						const { data } = this.state;
						this.setState({
							sortKey: key,
							sortBy,
							sortOrder,
							...this._sortData(data, sortBy, sortOrder),
						});
				}}
			}}>{props.children} {showLang ? <span className={'-i18n'}>({Constants.Locales[user.locale]})</span> : null}</td>;
		}
	};

	/**
	 * @return {object<string, boolean|function>}
	 * @private
	 */
	_getCheckableMap() {
		const {
			checkable: deletable, // 舊的 property, 實際上只拿來檢查 delete
			checkableMap,         // 新的 property, key-value 定義多種操作的 checkable. key 請對齊 userPrivileges 的 key
		} = this.props;
		const { userPrivileges } = this.state;
		return _.transform({
			del: d => d.$mgt === false ? false /* 資料層權限攔截 */ : !deletable || deletable(d),
			...checkableMap,
		}, (acc, value, key) => {
			if (userPrivileges[key] === false) acc[key] = false; // UI 層權限攔截
			else if (_.isBoolean(value) || _.isFunction(value)) acc[key] = value;
		}, {});
	}

	static NA = <small className={'color-lightGray'}>N/A</small>;
	TD = {
		subIndicator: props => {
			if(!props.data._sub) props.data._sub = {
				active: false,
				data: null
			};
			const {active, data} = props.data._sub;
			const {ipe} = this.state;
			return <td className={'-si'}>
				{ipe ? null : <i {...{
					className: `-sb btn btn-white fa fa-folder-tree ${active ? 'active' : ''}`,
					onClick: evt => {
						props.data._sub.active = !active;
						this.forceUpdate();
					}
				}} />}
			</td>;
		},
		mergeRow:props=> {
			const { mapKey,data ,rowSpan, map, style }  =props;
			let row = map[mapKey] ? null : <td  style={{...style, ...rowSpan==1?{ }:{ backgroundColor:'white'}}}  rowSpan={rowSpan}>{data}</td>;
			map[mapKey] = true;
			return row;
		},
		split:props=>{
			return <td >
				{props.data && props.data.length ? props.data.map((d,idx)=><div key={d} style={{paddingBottom:0,borderBottom:idx<props.data.length-1?'1px solid #EEE':''}}>{d}</div>) :null}
			</td>;
		},
		checkbox: props => {
			const {data: d, disable } = props;
			const { ipe, delIds } = this.state;
			const checkableMap = this._getCheckableMap();
			const isCheckable = _.some(checkableMap, (fn) => fn === false ? false : fn === true || fn(d, this));
			return <td className={'-chk'}>
				{ipe ? null
					: isCheckable && !disable && !delIds
						? <span className={`checkbox ${d._checked ? ' checked' : ''}`} onClick={evt => {
							const { preCheckedIds, checkedData } = this.state;
							d._checked = !d._checked;
							this.setState({
								preCheckedIds: d._checked ? _.union(preCheckedIds, [d.id]) : _.without(preCheckedIds, d.id),
								checkedData: d._checked ? _.union(checkedData, [d]) : _.without(checkedData, d),
							});
						}} />
						: <span className={'checkbox disable'} />
				}
			</td>;
		},
		ctrls: props => {
			const {data, edit, del, title} = props;
			const {delIds, ipe, userPrivileges: { edit: pEdit, del: pDel } } = this.state;
			const { mapKey ,rowSpan, map } = props;
			const newStyle = { ...map && rowSpan!=1 ? { backgroundColor:'white'}:{} };
			if(this._operatingBehavior === 'ipe' && ipe){
				if(data.id != ipe.id) return <td className={'-ctrl'} {...map ? { style: newStyle } : null} />;

				const {ipeing} = this.state;
				const _doIpe = (handler, evt) => {
					const {ipeForm:f} = this._dom;
					if(!f.validate()) return;
					const {api, method, data:_payload, onSuccess, errMsg, errCode, onError} = handler;
					this._ajax.ipe = new _Ajax({
						url: api,
						method: method || 'post',
						data: {
							... _payload ? _payload(f.getValues(), data) : f.getValues(),
							id: data.id,
						},
						noticeContainer: this._dom.ipeRow,
						noticeOptions: {
							stick: {
								position: 'center',
								edge: 'center',
							},
							inject: 'element',
						},
						errMsg, errCode,
						onRequest: () => this.setState({ipeing: true}, () => f.lock()),
						onSuccess: (data, xhr) =>
							this.setState({ipeing: false, ipe: null}, () => {
								this.reload();
								if(_.isFunction(onSuccess)) onSuccess(data, xhr);
							}),
						onError: (xhr) => {
							this.setState({ipeing: false}, () => {
								f.unlock();
								if(_.isFunction(onError)) onError(data, xhr);
							});
						},
					}).send();
				};
				return <td className={'-ctrl'}>
					<div className={'btn-group btn-group-white'}>
						{ipeing ? <Loading /> : null}
						{ipeing || ipe != 'add' ? null : <span {... {
							className: '-sb btn green fa fa-check',
							onClick: _doIpe.bind(this, this.ipeHandler.add),
							onMouseEnter: evt => this.setState({ctrlHint: {ico: 'check', hint: OptionUtils.getLabel('global.normal', 'add'), theme: 'green'}}),
							onMouseLeave: evt => this.setState({ctrlHint: null}),
						}} />}
						{ipeing || !_.isObject(ipe) ? null : <span {... {
							className: '-sb btn fa fa-check',
							onClick: _doIpe.bind(this, this.ipeHandler.edit),
							onMouseEnter: evt => this.setState({ctrlHint: {ico: 'check', hint: OptionUtils.getLabel('global.normal', 'modify')}}),
							onMouseLeave: evt => this.setState({ctrlHint: null}),
						}} />}
						{ipeing ? null : <span {... {
							className: '-sb btn red fa fa-times',
							onClick: evt => this.setState({ipe: false}),
							onMouseEnter: evt => this.setState({ctrlHint: {ico: 'times', hint: OptionUtils.getLabel('global.normal', 'cancel'), theme: 'red'}}),
							onMouseLeave: evt => this.setState({ctrlHint: null}),
						}} />}
					</div>
				</td>;
			}

			const isEdit = _.isFunction(edit) ? edit(data) : edit !== false;
			const isEditEnable = !pEdit ? false : data.$edit === false ? false : _.isPlainObject(edit) && _.isFunction(edit.enable) ? edit.enable(data) : true;
			const isDel = _.isFunction(del) ? del(data) : del !== false;
			const isDeling = !_.isNil(delIds) && (1 || delIds == data.id || delIds.includes(data.id));
			const isDelEnable = !pDel ? false : data.$mgt === false ? false : _.isPlainObject(del) && _.isFunction(del.enable) ? del.enable(data) : this.props.checkable(data);
			const titleLabel = _.isString(title) ? title : null;
			const _renderExtCtrl = (key) => {
				const isPermitted = this.state.userPrivileges[key];
				const prop = props[key];
				const isProp = _.isFunction(prop) ? prop(data) : prop !== false;
				const isPropEnable = !isPermitted ? false :_.isPlainObject(prop) && _.isFunction(prop.enable) ? prop.enable(props.data) : true;
				const ico = (prop && prop.ico) || 'cog';
				const hint = (prop && prop.hint) || OptionUtils.getLabel('global.normal', 'config');
				return isProp ? <span {...{
					key: key,
					className: `-sb btn fa fa-${ico} ${isPropEnable ? '' : 'disable'}`,
					... isPropEnable ? {onClick: evt => {
						this.state[key + 'Dlg'] = data;
						this.forceUpdate();
					}} : null,
					... isPropEnable ? {onMouseEnter: evt => this.setState({ctrlHint: {ico: ico, hint: hint}})} : null,
					... isPropEnable ? {onMouseLeave: evt => this.setState({ctrlHint: null})} : null,
				}} /> : null;
			};
			const _editBtn = <span {...{
				className: `-sb btn fa fa-pen-alt ${isEditEnable ? '' : 'disable'}`,
				... isEditEnable ? {onClick: evt =>
						this.setState(this._operatingBehavior === 'ipe' ? {ipe: data, ctrlHint: null} : {editDlg: data})} : null,
				... isEditEnable ? {onMouseEnter: evt => this.setState({ctrlHint: {ico: 'pen-alt', hint: OptionUtils.getLabel('global.normal', 'edit')}})} : null,
				... isEditEnable ? {onMouseLeave: evt => this.setState({ctrlHint: null})} : null,
			}} />;
			const _delBtn = <span {...{
				className: `-sb btn red ${isDelEnable ? '' : 'disable'} ${isDeling ? 'loading' : 'fa fa-trash-alt'}`,
				... isDelEnable ? {onClick: evt => {
						if(this.state.delIds) return;
						const msg = (
							del && _.isString(del.confirmMsg) ? del.confirmMsg :
								del && _.isFunction(del.confirmMsg) ? del.confirmMsg(data) : null
						) || OptionUtils.getLabel('components.grids', 'sureDeleteIt');
						this.setState({confirm: {
							ids: [data.id],
							msg,
						}});
					}} : null,
				... isDelEnable ? {onMouseEnter: evt => this.setState({ctrlHint: {ico: 'trash-alt', hint: OptionUtils.getLabel('global.normal', 'delete'), theme: 'red'}})} : null,
				... isDelEnable ? {onMouseLeave: evt => this.setState({ctrlHint: null})} : null,
			}} />;
			let cell = map && map[mapKey] ? null : <td {...map ? { style: newStyle } : null} className={'-ctrl'} rowSpan={rowSpan? rowSpan:1} title={titleLabel}>
			<div className={'btn-group btn-group-white'}>
				{isDeling ? <Loading /> : null}
				{isEdit && !isDeling ? _editBtn : null}
				{this.props.extDialogs && !isDeling ? _.keys(this.props.extDialogs).map(key => _renderExtCtrl(key)) : null}
				{isDel && !isDeling ? _delBtn : null}
			</div>
		</td>;
			if( map ) map[mapKey] = true;
			return  cell;
		},
		browse: props => {
			const { ipe, userPrivileges: { query }, delIds } = this.state;
			const { mapKey ,rowSpan, map } = props;
			const newStyle = { ...map && rowSpan!=1 ? { backgroundColor:'white'}:{} };
			let cell = map && map[mapKey] ? null : (ipe ? <td {...map ? { style: newStyle } : null} rowSpan={rowSpan? rowSpan:1}>{props.children}</td> : <td {...map ? { style: newStyle } : null} rowSpan={rowSpan? rowSpan:1}>
			<span {...query ? {
					className: `link${delIds ? ' disable' : ''}`,
					onClick: delIds ? null : () => this.setState({
					browseDlg: props.hasOwnProperty('editable') ? { idx: props.idx, editable: props.editable } : props.idx,
				}),
			} : null}>
				{props.children}
			</span>
			</td>);
			if( map ) map[mapKey] = true;
			return cell;
		},
		seq: props => {
			const { mapKey ,rowSpan, map, style } = props;
			const newStyle = { ...style ? style : {} , ...map && rowSpan!=1 ? { backgroundColor:'white'}:{} };
			if( !this.state.preIdx ) this.state.preIdx = 1;
			let idx = map ? this.state.preIdx : props.idx + 1;
			let cell = map && map[mapKey] ? null : <td rowSpan={rowSpan ? rowSpan :1} className={'-seq'} {...props.style || map ? { style: newStyle } : null} >{idx}</td>;

			if ( map && !map[mapKey]) this.state.preIdx  = this.state.preIdx  +1;
			if( map ) map[mapKey] = true;
			return cell;
		},
		i18n: props => {
			const d = props.data || props.children;
			return <td {...props.style ? { style: props.style } : null}>{props.isDenied ? '******' : d ? LocaleUtils.i18n(d) : ''}</td>;
		},
		adapt: props => {
			const d = props.data || props.children;
			return <td {...props.style ? { style: props.style } : null}>{props.isDenied ? '******' : d ? LocaleUtils.adapt(d) : ''}</td>;
		},
		date: props => {
			const { mapKey ,rowSpan, map, style } = props;
			const d = Utils.Format.date(props.data);
			const newStyle = { ...style ? style : {} , ...map && rowSpan!=1 ? { backgroundColor:'white'}:{} };
			let cell = map && map[mapKey] ? null : <td rowSpan={rowSpan ? rowSpan :1}className={'text-sm'} {...props.style || map ? { style: newStyle } : null}>{d ? d : BasicGrid.NA}</td>;
			if( map ) map[mapKey] = true;
			return cell;
		},
		dateRange: props => {
			if(!props || !_.isArray(props.data)) return <td>{BasicGrid.NA}</td>;
			const d0 = Utils.Format.date(props.data[0]);
			const d1 = Utils.Format.date(props.data[1]);
			return <td className={'text-sm'} {...props.style ? { style: props.style } : null}>{d0 ? d0 : BasicGrid.NA} ~ {d1 ? d1 : BasicGrid.NA}</td>;
		},
		YYYYMMRange: props => {
			if(!props || !_.isArray(props.data)) return <td>{BasicGrid.NA}</td>;
			const d0 = Utils.Format.YYYYMM(props.data[0]);
			const d1 = Utils.Format.YYYYMM(props.data[1]);
			return <td className={'text-sm'} {...props.style ? { style: props.style } : null}>{d0 ? d0 : BasicGrid.NA} ~ {d1 ? d1 : BasicGrid.NA}</td>;
		},
		datetimeRange: props => {
			if(!props || !_.isArray(props.data)) return <td>{BasicGrid.NA}</td>;
			const d0 = Utils.Format.dateTime(props.data[0], false);
			const d1 = Utils.Format[moment(props.data[0]).isSame(moment(props.data[1]), 'day') ? 'time' : 'dateTime'](props.data[1], false);
			return <td className={'text-sm'} {...props.style ? { style: props.style } : null}>{d0 ? d0 : BasicGrid.NA} ~ {d1 ? d1 : BasicGrid.NA}</td>;
		},
		datetime: props => {
			const d = Utils.Format.dateTime(props.data, props.second);
			return <td className={'text-sm'} {...props.style ? { style: props.style } : null}>{d ? d : BasicGrid.NA}</td>;
		},
		locale: props => <td {...props.style ? { style: props.style } : null}>{Constants.Locales[props.data]}</td>,
		timezone: props => {
			const d = Utils.Format.dateTime(props.data, props.second, props.timezone);
			return <td className={'text-sm'} {...props.style ? { style: props.style } : null}>{d ? d : BasicGrid.NA}</td>;
		},
		label: props => {
			// props.isDenied ? '******' :
			const v = OptionUtils.getLabel((props._key || props.oKey), props.data);
			return <td {...props.className && {className: props.className}} {...props.style ? { style: props.style } : null}>{v ? v : BasicGrid.NA}</td>;
		},
		label2: ({ style, comp, data }) =>
			<td style={style}>{comp?.label?.(fixList(data)) || BasicGrid.NA}</td>,
		userLabel: props => {
			const obj = _.find(props.collection, {id: props.id});
			return <td {...props.style ? { style: props.style } : null}>{obj ? (obj.name || obj.label || obj.text) : BasicGrid.NA}</td>;
		},
		optionLabelArr: props => {
			console.warn('[Legacy Component] <TD.optionLabelArr> will be removed soon! Use <TD.getJoinedLabels> instead.');
			let dataArr = _.isArray(props.data) ? props.data :
				(props.data && props.data.length > 0) ? props.data.split(',') : props.data;
			let value = [];
			_.forEach(dataArr, item => {
				let out = OptionUtils.getLabel(props.oKey, item);
				value.push(out);
			});
			return <td {...props.style ? { style: props.style } : null}>{value.join(',')}</td>;
		},
		getJoinedLabels: ({ oKey, data, style }) => <td {...style ? { style } : null}>{OptionUtils.getJoinedLabels(oKey, data)}</td>,
		active: props => {
			const {active, children} = props;
			const a = !_.isUndefined(active) ? active : children;
			return this.state.ipe ? <td {...props.style ? { style: props.style } : null}>{OptionUtils.getLabel('global.usedStatus', a)}</td> : <td>
				<span className={`color-${a ? 'green' : 'gray'}`}>
					<i className={`far fa-fw fa-${a ? 'check' : 'snooze'}`} />
					{OptionUtils.getLabel('global.usedStatus', a)}
				</span>
			</td>;
		},
		cardMatch: props => {
			const color = ['gray', 'green', 'red'];
			const ico = ['clock', 'check', 'exclamation-triangle'];
			return <td {...props.style ? { style: props.style } : null}>
				<span className={`color-${color[props.cardMatch]}`}>
					<i className={`far fa-fw fa-${ico[props.cardMatch]}`} />
					{OptionUtils.getLabel('am.cardMatch.gridStatus', props.cardMatch)}
				</span>
			</td>;
		},
		boolean: props => <td className={`color-${props.value ? 'green' : 'lightGray'}`}>
			<i className={`far fa-fw fa-check`} />
		</td>,
		textarea: props => <td><textarea {... {
			className: 'read',
			disabled: 'disabled',
			rows: props.line || 2,
			defaultValue: props.text || props.children
		}} /></td>,
		numeric: props => {
			const {numeric, fixed} = Utils.Format;
			const d = numeric(fixed(props.data, props.fixDigits ? props.fixDigits : 4)) || props.defs;
			return <td {...{
				className: `text-right ${d > 0 ? props.plus || '' : d < 0 ? props.minus || '' : ''}`,
				...props.style ? { style: props.style } : null,
			}}>
				{d || d === 0 ? <>{props.prefix || ''}{d}{props.suffix || ''}</> : BasicGrid.NA}
			</td>;
		},
		denied: props => {
			const { isDenied } = props;
			let value = props.value || props.children;
			value = isDenied || value === -403 ? '******' : value;
			return <td {...props.style ? { style: props.style } : null}>{value}</td>;
		},
	};
	Manipulator = {
		// del: Manipulators.del,
		/** @deprecated 舊的都改掉的話，再換成 Manipulators.del */
		del: props => {
			const checkedData = props.data;
			const _multiDelete = evt => {
				if(this.state.delIds) return;
				const { confirmMsg } = {...this.deleteHandler, ...props};
				const msg = (
					_.isString(confirmMsg) ? confirmMsg :
					_.isFunction(confirmMsg) ? confirmMsg(checkedData) : null
				) || _.template(getLabel('components.grids', 'deleteCountMsg'))({'count': checkedData.length});
				const ids = _.map(checkedData, d => d.id);
				this.setState({confirm: {ids, msg}});
			};
			const {delIds} = this.state;
			return <span className={`btn btn-danger fa fa-trash-alt ${delIds ? 'disabled' : ''}`} onClick={_multiDelete}>
				{OptionUtils.getLabel('global.normal', 'delete')}
				<small>({props.exText}{checkedData.length})</small>
			</span>;
		}
	};

	constructor(props){
		super(props);
		const { initSort, data, newDialog, arrangeDialog, extDialogs, userPrivileges = {} } = this.props;

		if (initSort) {
			this.state.sortBy = this.state.sortKey = initSort.key || null;
			this.state.sortOrder = initSort.order || null;
		}

		_.each(['query', 'edit', 'del', ..._.keys(extDialogs)], key => {
			const fn = userPrivileges[key];
			this.state.userPrivileges[key] = _.isNil(fn) || AuthUtils.chkPrivilege(fn);
		});

		const onSuccess = newDialog && newDialog.props.onSuccess;
		this._NewDialog = nprops => newDialog ? React.cloneElement(newDialog, {
			onSuccess: _.isFunction(onSuccess) ? onSuccess : (data, xhr) => this.reload(),
			close: () => this.setState({newDlg: false}),
			doEdit: i => {
				this.setState({newDlg: false});
				this.reload();
				this.toEdit = i;
			},
			parent: this,
			...nprops,
		}) : null;
		const onSuccess1 = arrangeDialog && arrangeDialog.props.onSuccess;
		this._ArrangeDialog = arrangeDialog ? React.cloneElement(arrangeDialog, {
			onSuccess: _.isFunction(onSuccess1) ? onSuccess1 : (data, xhr) => this.reload(),
			close: () => this.setState({arrDlg: false}),
			parent: this,
			readOnly: !this.state.userPrivileges.edit,
		}) : null;

		if (data) {
			const { dataFilter, sift, rowsPerStepping, freezing, freezeHeader  } = this.props;
			const { sortBy, sortOrder } = this.state;
			Object.assign(this.state, this._sortData(data, sortBy, sortOrder));
		}

		this.__loadByBasicCriteria = this.__loadByBasicCriteria.bind(this);		// TODO: old version, eliminate it soon!
		this.__loadByAdvCriteria = this.__loadByAdvCriteria.bind(this);			// TODO: old version, eliminate it soon!
		this.__fetchData = this.__fetchData.bind(this);							// TODO: old version, eliminate it soon!

		this.fetch = this.fetch.bind(this);
		this.refresh = this.refresh.bind(this);
		this.reload = () => {
			if (!this.state.delIds)
			this.refresh(true); // Fetch data again whatever ...
		};
		this._stepData = this._stepData.bind(this);
		if (props.deleteHandler) this.deleteHandler = props.deleteHandler(this);
	}

	componentDidMount() {
		super.componentDidMount();

		const {data} = this.state;
		if(this.renderBasicCriteria){	// TODO: old version, eliminate it soon!
			if(!data) this.reload();
			else {
				this._fixContentHeight();
				this._stepData();
			}

			_.forEach(this.basicCriteria, input => {
				if(input instanceof Picker || input instanceof DepPicker){
					if(input.menuProps){
						input.menuProps.inject = this._dom.root;
						input.menuProps.width = 0;
					}
					else input.menuProps = {inject: this._dom.root, width: 0};
				}
			});
			return;
		}

		if(!data) this.fetch();
		else {
			this._fixContentHeight();
			this._stepData();
		}
	}

	componentWillUnmount() {
		super.componentWillUnmount();
		_.each(this.state.data, d => delete d._checked);
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		this.state.preIdx = 0;
		this._fixContentHeight();
		const wrapper = this._dom.gridWrapper;
		if(wrapper){
			const {ipe} = this.state;
			if(ipe === 'add') wrapper.scrollTop = wrapper.scrollHeight;
		}
		this._stepData();

		if (!this.state.loading && this.toEdit) {
			const dataId = this.toEdit;
			this.toEdit = void 0;
			const { data } = this.state;
			if (!_.isEmpty(data)) {
				const row = data.find(d => d.id === dataId);
				if (row) {
					this.setState({ editDlg: row });
				}
			}
		}
	}

	_fixContentHeight() {
		const wrapper = this._dom.gridWrapper;
		if (wrapper) {
			const parent = this.props.container || this._dom.root.getParent();
			const paddingBottom = parent.getComputedSize()['padding-bottom'];
			wrapper.setStyle('maxHeight', `calc(100% - ${wrapper.getCoordinates(parent).top - paddingBottom}px)`);
		}
	}

	refresh(force = false){	// NOT fetch data again if criteria is consistent
		if(this.renderBasicCriteria) {	// TODO: old version, eliminate it soon!
			const {criteriaType: t, openAdvPanel} = this.state;
			if (t === 'basic') this.__loadByBasicCriteria(force);
			else if (t === 'adv' && openAdvPanel) this.__loadByAdvCriteria(force);
			else if (t === 'adv' && !openAdvPanel && force) this.__fetchData(this._preAdvCriteria);
			else this.__fetchData();
			return;
		}

		const {_criteria:c, _preCriteria:pc, _preAdvCriteria:pac, criteriaType:t} = this.state;
		if(force || !((t === 'basic' && _.isEqual(pc, c)) || (t === 'adv' && _.isEqual(pac, c)))) {
			if (_.isNil(this.props.data))
				this.fetch();
			else
				this.filter();
		}
	}

	// TODO: old version, eliminate it soon!
	__loadByBasicCriteria(force = false){
		const {_preCriteria, _criteria} = this;
		if(!force && _.isEqual(_preCriteria, _criteria)) return;
		else this.__fetchData();
	}
	// TODO: old version, eliminate it soon!
	__loadByAdvCriteria(force = false){
		this._preAdvTips = [];
		this._criteria = {};
		_.keys(this.advCriteria).map(key => {
			const input = this.advCriteria[key];
			if(input && input.props.tip) {
				const _v = input.getValue();
				this._criteria[key] = _v;
				if (_clear(_v)){
					this._preAdvTips.push(_.isFunction(input.props.tip) ? input.props.tip() : input.props.tip);
				}
			}
		});
		if(!force && _.isEqual(this._preAdvCriteria, this._criteria))
			return this.setState({openAdvPanel: false});

		this.setState({ criteriaType: 'adv', openAdvPanel: false }, this.__fetchData);
	}
	// TODO: old version, eliminate it soon!
	__fetchData(){
		let { _criteria } = this;
		const {api, query, method, fetchErrMsg, fetchErrCode, onLoadSuccess, onError, rowsPerStepping } = this.props;
		const {criteriaType} = this.state;
		const criteria = {
			... _.pickBy(_criteria, _clear),
			$criteriaType: criteriaType,
		};
		this._ajax.fetch = new _Ajax({
			url: api,
			method: method || 'post',
			data: {
				... this._operatingBehavior === 'ipe' ? null : criteria,
				... query,
			},
			noticeContainer: this._dom.root,
			onRequest: () => this.setState({
				loading: true,
				data: null,
				checkedData: [],
				error: false,
				steppedRows: rowsPerStepping,
			}),
			onSuccess: (data, xhr) => {
				const { preCheckedIds: _preCheckedIds, sortBy, sortOrder } = this.state;
				const preCheckedIds = _.intersection(_preCheckedIds, _.map(data, 'id'));
				const checkedData = [];
				data.forEach(d => {
					d._checked = _.includes(preCheckedIds, d.id);
					if (d._checked) checkedData.push(d);
				});
				this.setState({
					loading: false,
					...this._sortData(data, sortBy, sortOrder),
					preCheckedIds,
					checkedData,
					error: false
				}, () => {
					if(criteriaType === 'basic') this._preCriteria = _.clone(_criteria);
					else if (criteriaType === 'adv') this._preAdvCriteria = _.clone(_criteria);
					if(_.isFunction(onLoadSuccess)) onLoadSuccess(data);
				});
			},
			errMsg: fetchErrMsg,
			errCode: fetchErrCode,
			onError: (xhr, msg) => {
				this.setState({
					loading: false,
					data: null,
					error: msg
				}, () => _.isFunction(onError) ? onError() : null);
			}
		}).send();
	}

	// 目前只保證在資料來源為 props.data 且無透過 api 撈取資料的情況下可用。
	filter() {
		const { localFilter, dataFilter, sift, rowsPerStepping, freezing, freezeHeader } = this.props;
		if (!_.isFunction(localFilter)) return;

		const { _criteria, data: allData, preCheckedIds: oldPreCheckedIds } = this.state;
		const filteredData = _.isEmpty(_criteria) ? allData : _.filter(allData, _data => localFilter(_criteria, _data));
		// NOTE 原本的 data 已經 sorted 了，不用再排序

		// migrate checked
		if (!_.isEmpty(oldPreCheckedIds)) {
			const checkedData = [];
			this.state.preCheckedIds = _.chain(filteredData).filter('_checked').map('id').value();
			_.each(allData, _data => {
				_data._checked = _.includes(this.state.preCheckedIds, _data.id);
				if (_data._checked) checkedData.push(_data);
			});
			this.state.checkedData = checkedData;
		}

		this.state.filteredData = filteredData;
		this.state.steppedRows = rowsPerStepping;
		this.state.renderData = _pickRenderData(filteredData, dataFilter, sift, !freezing && freezeHeader ? rowsPerStepping : -1);
		this.forceUpdate();
	}

	fetch(){
		const {api, query, method, fetchErrMsg, fetchErrCode, onLoadSuccess, onError, rowsPerStepping } = this.props;
		const {criteriaType, _criteria} = this.state;
		if(this._ajax.fetch && this._ajax.fetch.isRunning()) this._ajax.fetch.cancel();
		this._ajax.fetch = new _Ajax({
			url: api,
			method: method || 'post',
			data: _.pickBy(this._operatingBehavior === 'ipe' ? query : {
				$criteriaType: criteriaType,
				... _criteria,
				... query,
			}, _clear),
			noticeContainer: this._dom.root,
			onRequest: () => this.setState({
				loading: true,
				data: null,
				checkedData: [],
				error: false,
				steppedRows: rowsPerStepping,
			}, () => {
				const MapComponent = this.props.fetchComponent;
				if (MapComponent) MapComponent.fetch(MapComponent.defaultProps);
			}),
			onSuccess: (_data, xhr) => {
				const { handleData } = this.props;
				const { data, ...otherData } = handleData(_data);
				const { sortBy, sortOrder } = this.state;
				const checkableMap = this._getCheckableMap();
				const preCheckedIdMap = _.keyBy(this.state.preCheckedIds);
				const preCheckedIds = [];
				const checkedData = [];
				data.forEach(d => {
					d._checked = !!preCheckedIdMap[d.id] && _.some(checkableMap, (fn) => fn === false ? false : fn === true || fn(d, this));
					if (d._checked) {
						preCheckedIds.push(d.id);
						checkedData.push(d);
					}
				});
				this.setState({
					loading: false,
					...this._sortData(data, sortBy, sortOrder),
					... criteriaType === 'basic' ?
						{_preCriteria: _.cloneDeep(_criteria)} :
						{_preAdvCriteria: _.cloneDeep(_criteria)},
					preCheckedIds,
					checkedData,
					error: false,
					otherData
				}, () => _.isFunction(onLoadSuccess) ? onLoadSuccess(data) : null);
			},
			errMsg: fetchErrMsg,
			errCode: fetchErrCode,
			onError: (xhr) => {
				this.setState({
					loading: false,
					data: null,
					error: true,
				}, () => _.isFunction(onError) ? onError() : null);
			}
		}).send();
	}

	openNewDialog(p) { if (!this.state.delIds) this.setState({ newDlg: p || true }); }
	openArrangeDialog() { if (!this.state.delIds) this.setState({ arrDlg: true }); }
	openDialog(key, dataId) {
		if (this.state.delIds) return;
		const _open = () => {
			let data = _.find(this.state.data, { id: dataId });
			if (!data) data = { id: dataId };
			this.state[key + 'Dlg'] = data;
			this.forceUpdate();
		};
		if (this.state[key + 'Dlg']) {
			this.setState({ [key + 'Dlg']: false }, _open);
		} else _open();
	}

	setSelectedIds(ids) {
		if (_.isEmpty(this.state.data) || _.isEmpty(ids)) ids = [];
		const mapping = _.transform(ids, (acc, id) => { acc[id] = true; }, {});
		const checkedData = _.reduce(this.state.data, (acc, data) => {
			// mutate data
			data._checked = !!mapping[data.id];
			if (data._checked) acc.push(data);
			return acc;
		}, []);
		this.setState({ checkedData });
	}
	getSelectedIds() {
		return _.map(this.state.checkedData, 'id');
	}
	getSelectedData() {
		return [...this.state.checkedData];
	}
	getCurrentData() {
		const {sift} = this.props;
		const {data} = this.state;
		return sift ? sift(data) : data;
	}
	getFilteredData() {
		const { sift } = this.props;
		const { filteredData, data } = this.state;
		const _data = filteredData || data;
		return sift ? sift(_data) : _data;
	}
	isBasic() { return this.state.criteriaType === 'basic'; }
	isAdv() { return this.state.criteriaType === 'adv'; }
	getCurrentCriteria(){	// TODO: old version, eliminate it soon!
		const {_preCriteria, _preAdvCriteria} = this;
		const {criteriaType, sortKey, sortOrder} = this.state;
		return {
			criteriaType,
			criteria: criteriaType === 'basic' ? _preCriteria : criteriaType === 'adv' ? _preAdvCriteria : {},
			sortKey, sortOrder
		};
	}

	// abstract methods ...
	renderGridHeader() {}
	renderGridBody(data, idx, allData) {}
	renderGridFooter(allData) {}
	renderEditRow(data, idx, inputs) {}
	renderSubRow(row, sub, idx) {
		const { gridSub: s } = this.props;
		return typeof s === 'function' ? s(row, idx) : <i>&nbsp;</i>;
	}
	renderFixRow() {}

	/**
	 * 計算各個 Manipulator 按鈕是否可用
	 * @param {object[]} checkedData
	 * @return {object<string, boolean>}
	 * @private
	 */
	_calcActives(checkedData = this.state.checkedData) {
		const checkableMap = this._getCheckableMap();
		return _.transform(checkableMap, (acc, fn, key) => {
			if (_.isBoolean(fn)) acc[key] = fn;
			else acc[key] = _.every(checkedData, _data => fn(_data, this));
		});
	}

	renderManipulator(checkedData) {
		const { Manipulator } = this;
		const { delIds, exText } = this.state;
		const actives = this._calcActives(checkedData);
		const btns = _.map(Manipulator, (_Btn, key) => {
			const isActive = actives[key];
			// NOTE 刪除進行中也不能使用 Manipulator
			const props =
				{ key, _self: this, disabled: isActive === false || !!delIds, data: checkedData, exText };
			if (_.isPlainObject(_Btn)) return <Manipulators.default {..._Btn} {...props} />;
			return <_Btn {...props} />;
		});
		return btns.length === 0 ? null : <>
			{btns.length === 1 ? btns[0] : <div className="btn-group">{btns}</div>}
			<i className={'-split'} />
		</>;
	}

	deleteHandler = null;
	_deleteAjax(ids){
		if(!this.deleteHandler) throw Error('[Grid] "deleteHandler" not found!');
		const { api, method, onRequest, onSuccess, successMsg, onError, errMsg, errCode, root } =
			this.deleteHandler;
		const onDeleteSuccess = this.props.onDeleteSuccess;
		const { slowDel } = this.props, counts = { success: 0, fail: 0, used: 0, unexpected: 0 };
		const noticeContainer = this._dom.root, rt = root || noticeContainer;
		const fMap = {
			onSuccess: (data, xhr) => {
				Object.keys(counts).forEach(k => counts[k] += (+data[k] || 0));
				if (slowDel && data.success === slowDel && ids.length) {
					return this.setState({ exText: counts.success + '/' }, () => fMap.progress().send());
				}
				Object.assign(data, counts);
				if (typeof onDeleteSuccess == 'function') onDeleteSuccess(data, xhr);
				else this.setState({ delIds: null, exText: ''}, () => {
					const { success, fail, used, unexpected } = data;
					this.reload();
					if (successMsg && (success > 0 || fail > 0 || used > 0)) {
						Utils.notice(rt, success === 1 && !fail && !used && !unexpected ? successMsg : [
							successMsg, _.template(getLabel('components.grids', success > 1 ? 'successCounts' : 'successCount'))({'success': success}), fail > 0 ? _.template(getLabel('components.grids', fail > 1 ? 'failCounts' : 'failCount'))({'fail': fail}) : '',
							used > 0 ? _.template(getLabel('components.grids', used > 1 ? 'usedCounts':'usedCount'))({'used': used}) : '',
							unexpected > 0 ? _.template(getLabel('components.grids', unexpected > 1 ? 'errorCounts' : 'errorCount'))({'unexpected': unexpected}) : '', ')',
						].join(''), fail > 0 || used > 0 || unexpected > 0 || 'success');
					}
					if (typeof onSuccess == 'function') onSuccess(data, xhr);
				});
			},
			onError: xhr => this.setState({ delIds: null },
				() => typeof onError == 'function' && onError(xhr)),
			progress: () => this._ajax.del = new _Ajax({
				url: api, method: method || 'post', data: { ids: slowDel ? ids.splice(0, slowDel) : ids },
				onSuccess: fMap.onSuccess, onError: fMap.onError, errMsg, errCode, noticeContainer,
			}),
		};
		this.setState({ delIds: ids.slice(0) }, () => typeof onRequest == 'function' && onRequest());
		return fMap.progress();
	}

	getIteratees(sortBy) {
		return _data => {
			const path = sortBy.split('.');
			const v = _.get(_data, path);
			return v === -403 ? undefined : v;
		};
	}

	static _orderBy(list, iter, order) {
		const rng = Object.values(list || 0);
		if (rng.length) {
			const fact = order == 'desc' ? -1 : 1;
			return rng.sort((a, b) => {
				const [a1, b1] = [a, b].map(iter);
				const [a2, b2] = [a1, b1].map(v1 => typeof v1);
				if (a2 == 'string') {
					if (b2 == 'string') {
						const [a3, b3] = [a1, b1].map(v2 => v2.toUpperCase());
						return a3 == b3 ? 0 : a3 > b3 ? fact : -fact;
					} else if (b2 == 'number') return -fact;
					else return fact;
				} else if (a2 == 'number' && !isNaN(a1)) {
					if (b2 != 'number') return fact;
					else return isNaN(b1) ? fact : a1 == b1 ? 0 : a1 > b1 ? fact : -fact;
				} else return b2 == 'string' || (b2 == 'number' && !isNaN(b1)) ? -fact : 0;
			});
		} else return list;
	}

	_stepData() {
		const { rowsPerStepping } = this.props;
		if (rowsPerStepping <= 0) return;
		const wrapper = this._dom.gridWrapper;
		if (!wrapper) return;
		const { data: allData, filteredData: allFilteredData, steppedRows } = this.state;
		let _allData = allFilteredData || allData;
		if (_allData.length <= steppedRows) return;
		const { y } = wrapper.getSize();
		const { scrollTop, scrollHeight } = wrapper;
		if (y + scrollTop >= (scrollHeight - 200)) { // 距離底部200px就再畫一次
			const { dataFilter, sift } = this.props;
			const newSteppedRows = steppedRows + rowsPerStepping;
			const { sortBy, sortOrder } = this.state;
			if (sortBy) {
				const iteratees = this.getIteratees(sortBy);
				_allData = _allData ? this.props.sortFunc(_allData, iteratees, sortOrder) : null;
			}
			this.setState({
				steppedRows: newSteppedRows,
				renderData: _pickRenderData(_allData, dataFilter, sift, newSteppedRows),
			});
		}
	}

	_sortData(data, sortBy, sortOrder) {
		const { freezing, freezeHeader, dataFilter, sift } = this.props;
		const { steppedRows } = this.state;
		let { filteredData } = this.state;
		if (sortBy) {
			const iteratees = this.getIteratees(sortBy);
			data = this.props.sortFunc(data, iteratees, sortOrder);
			filteredData = filteredData ? this.props.sortFunc(filteredData, iteratees, sortOrder) : null;
		}

		return {
			data,
			renderData: _pickRenderData(filteredData || data, dataFilter, sift, !freezing && freezeHeader ? steppedRows : -1),
		};
	}

	render(){
		const {
			theme, style,
			caption, ctrlPanel, extCtrls, isBasicCriteria:isBasic, isAdvCriteria:isAdv,
			api, isInfo, isRefreshBtn, isConfigBtn, isExportBtn,
			colWidths, fetch,
			extDialogs, emptyWarning,
			freezeHeader, freezing, freezingContainer,
			rowsPerStepping,
			className,
		} = this.props;
		const {
			criteriaType, loading, error, openAdvPanel,
			newDlg, editDlg, editDlgProps, browseDlg, arrDlg, ipe, confirm,
			data: allData, renderData: data, checkedData,
			steppedRows, filteredData, fixData, delIds,
		} = this.state;

		const _genEditDialog = (data, props) => {
			const {editDialog:dialog} = this.props;
			const onSuccess = dialog && dialog.props.onSuccess;
			return dialog ? React.cloneElement(dialog, {
				[fetch === false ? 'data' : 'dataFromGrid']: data,
				id: data.id,
				onSuccess: _.isFunction(onSuccess) ? onSuccess : (data, xhr) => this.reload(),
				close: () => this.setState({editDlg: false}),
				parent: this,
				... props,
			}) : null;
		};
		const _genBrowseDialog = browseDlg => {
			if(!allData) return null;
			const {browseDialog: dialog} = this.props;
			const { userPrivileges: { edit: pEdit } } = this.state;
			const idx = _.isNumber(browseDlg) ? browseDlg : browseDlg.idx;
			// !!! editable 非 function 定無法隨上下筆變動
			const editable = !!pEdit && browseDlg.editable !== false && (_data => _data.$edit !== false &&
				(typeof browseDlg.editable !== 'function' || browseDlg.editable(_data)));
			return dialog ? React.cloneElement(dialog, {
				idx, editable,
				dataSet: allData.map(d => ({id: d.id, [fetch === false ? 'data' : 'dataFromGrid']: d})),
				edit: (i, d) => this.setState({editDlg: d, editDlgProps: dialog.props.editProps, browseDlg: false}),
				close: () => this.setState({browseDlg: false}),
				parent: this,
			}) : null;
		};
		const _genExtDialog = (key, data) => {
			const dialog = extDialogs[key];
			const onSuccess = _.isFunction(dialog.props.onSuccess)
				? (data, xhr) => {
					dialog.props.onSuccess(data, xhr);
					this.reload();
				}
				: (data) => this.reload();
			return React.cloneElement(dialog, {
				key: key,
				...fetch === false ? {data} : {dataFromGrid: data},
				id: data.id,
				onSuccess,
				close: () => this.setState({[key + 'Dlg']: false}, () => _.isFunction(dialog.props.close) ? dialog.props.close() : null),
				parent: this,
			});
		};

		const _renderCtrlPanel = () => {
			const {basicCriteria, advCriteria, theme} = this.props;
			const {queryOnEditing, _criteria, _preCriteria, _preAdvCriteria, _criteriaDescriptor} = this.state;
			const _renderCriteriaBtn = () => {
				return (isBasic || isAdv) || (basicCriteria || advCriteria) ? <>
					<span className={'btn-group btn-group-white'} style={{margin:'3px 0'}}>
						{isBasic || basicCriteria ? <TipButton {... {
							tip: OptionUtils.getLabel('components.grids', 'simpleSearch'),
							className: `-sb btn fa fa-search ${criteriaType === 'basic' && !openAdvPanel ? ' active' : ''}`,
							onClick: evt => {
								if (criteriaType == 'basic' || delIds) return;
								this._criteria = {};
								this._preCriteria = {};
								this._preAdvCriteria = {};
								this.setState({
									criteriaType: 'basic',
									advState: null,
									_criteria: this.state._preCriteria,
								}, this.reload);
							}
						}} /> : null}
						{isAdv || advCriteria ? <TipButton {... {
							tip: OptionUtils.getLabel('components.grids', 'advancedSearch'),
							className: `-sb btn fa fa-sliders-h ${criteriaType === 'adv' || openAdvPanel ? 'active' : ''}`,
							onClick: evt => {
								if (criteriaType == 'adv' || delIds) return;
								if(_preAdvCriteria)
									this.setState({criteriaType: 'adv', openAdvPanel: false, _criteria: _preAdvCriteria}, this.reload);
								else this.setState({criteriaType: 'adv', openAdvPanel: true});
							},
						}} /> : null}
					</span>
					{criteriaType ? <small className={'fa fa-chevron-right color-lightGray'} style={{margin:'0 4px 0 6px'}} /> : null}
				</> : null;
			};
			const _renderAdvStateBtn = () => {
				if(!this.props.advState) return;
				const {advState:{init, items}} = this.props;
				const {advState} = this.state;
				const v = advState ? advState : init;
				if(!advState) this.state.advState = v;
				return <>
					<span className={'-advStateBtnGroup btn-group btn-group-white'}>
						{_.map(items, (value, key) => <span {... {
							key: key,
							className: `btn green ${v == key ? ' active' : ''}`,
							onClick: evt => {
								evt.stopPropagation();
								this.setState({advState: key});
							}
						}} >{value}</span>)}
					</span>
					<i className={'-split'} />
				</>;
			};
			return <div className={'-ctrlPanel'} ref={ele => this._dom.ctrlPanel = ele}>
				{checkedData.length > 0 && <div className={'-manipulator'}>
					{this.renderManipulator(checkedData)}
				</div>}
				{caption ? <span className={'-caption'}>{caption}</span> : null}
				{this.state.userPrivileges.query && <>
					{(isBasic || basicCriteria) && (!isAdv && !advCriteria) ? <i className={'-basicIcon fal fa-search'} /> : _renderCriteriaBtn()}
					{isBasic && criteriaType === 'basic' && (
						this.renderBasicCriteria ? <__BasicCriteriaPanel {... {	// TODO: old version, eliminate it soon!
								inputs: this.basicCriteria,
								fetch: this.__fetchData,
								_preCriteria: this._preCriteria,
								_criteria: this._criteria,
							}}>{this.renderBasicCriteria()}</__BasicCriteriaPanel> :
							<BasicCriteriaPanel {... {
								src: basicCriteria,
								onMounted: criteria => this.state._criteria = criteria,
								onEditing: criteria => {
									this.state._criteria = criteria;
									this.state.queryOnEditing = true;
									this.refresh();
								},
								onUpdate: criteria => {
									this.state._criteria = criteria;
									this.state.queryOnEditing = false;
									this.refresh();
								},
								disable: (!queryOnEditing && loading) || delIds,
								theme,
							}} />
					)}
					{criteriaType === 'adv' && openAdvPanel && <small className={'color-lightGray'}>{OptionUtils.getLabel('components.grids', 'advancedSearchConfig')}...</small>}
					{criteriaType === 'adv' && !openAdvPanel && (this.renderAdvCriteria ? <span {... {	// TODO: old version, eliminate it soon!
						className: '-advCriteria',
						... loading ? null : {onClick: evt => this.setState({openAdvPanel: true})},
					}}>
					<span className={loading ? '' : 'link'}>
						{/* {_.size(this._preAdvTips)} 個條件 */}
						<I18n key1='components.grids' key2='counditionCount' options={{
							count: _.size(this._preAdvTips)
						}} />
						{this._preAdvTips && this._preAdvTips.length > 0 ? `: (${this._preAdvTips})` : null}
					</span>
				</span> : <AdvCriteriaDescriptor {...{
						src: advCriteria,
						criteria: _.pickBy(_criteria, c => !_.isNil(c)),
						descriptor: _criteriaDescriptor,
							onClick: delIds ? null : () => this.setState({ openAdvPanel: true }),
							onRemove: delIds ? null : key => {
							_criteria[key] = undefined;
							if(_.size(_criteria) > 0) this.setState({_criteria}, this.reload);
							else this.setState({criteriaType: 'basic', _criteria: _preCriteria, _preAdvCriteria: null}, this.reload);
						},
					}} />)}

					{
						openAdvPanel ? (
							this.renderAdvCriteria ? <__AdvCriteriaPanel {...{	// TODO: old version, eliminate it soon!
									inputs: this.advCriteria,
									values: this._preAdvCriteria,
									stick: this._dom.ctrlPanel,
									onOk: this.__loadByAdvCriteria.bind(this),
									onCancel: () => {
										if (!_.isEmpty(this._preAdvCriteria)) this.setState({ openAdvPanel: false });
										else this.setState({
											criteriaType: 'basic',
											openAdvPanel: false
										}, () => this.refresh());
									}
								}}>{this.renderAdvCriteria()}</__AdvCriteriaPanel> :
								<AdvCriteriaBox {... {
									stick: this._dom.ctrlPanel,
									src: advCriteria,
									criteria: _criteria,
									descriptor: _criteriaDescriptor,
									onOk: (criteria, descriptor) => this.setState({
										openAdvPanel: false,
										_criteria: criteria,
										_criteriaDescriptor: descriptor,
									}, this.refresh),
									close: () => {
										if(_preAdvCriteria) this.setState({openAdvPanel: false});
										else this.setState({openAdvPanel: false, criteriaType: 'basic', _criteria: _preCriteria, page: 1}, this.refresh);
									},
								}} />
						) : null
					}
				</>}
				<div className={'-right'}>
					{criteriaType === 'adv' && !openAdvPanel && _renderAdvStateBtn()}
					{isInfo && <>
						{!loading && allData && theme != 'portal' && <small className={'msg sm gray50'}>
							<I18n key1='components.grids' key2={filteredData ? filteredData.length > 1 ? 'allCounts': 'allCount' : allData.length > 1 ? 'allCounts': 'allCount'} options={{'count': <strong className={'color-blue'}>{filteredData ? filteredData.length : allData.length}</strong>}} />
						</small>}
						{!loading && allData && theme === 'portal' && <small>
							<I18n key1='components.grids' key2={(allData.length + (fixData?.length || 0)) > 1 ? 'matchFewCounts' : 'matchFewCount'} options={{'count': <strong className={'color-blue'}>{allData.length + (fixData?.length || 0)}</strong>}} />
						</small>}
						{api && isRefreshBtn && !loading && <TipButton {... {
							tip: OptionUtils.getLabel('global.normal', 'refresh'),
							disabled: loading || delIds,
							className: '-refresh -sb1 btn btn-ico btn-white fa fa-redo',
							style: {marginLeft: 5},
							onClick: this.reload,
						}} />}
					</>}
					{(isExportBtn || isConfigBtn) && !loading && <>
						<i className={'-split'} />
						<span className={'btn-group btn-group-white'} style={{margin:0}}>
							{isExportBtn ? <TipButton {... {
								tip: OptionUtils.getLabel('global.normal', 'exportInfo'),
								disable: loading,
								className: '-sb1 btn fa fa-file-download'
							}} /> : null}
							{isConfigBtn ? <TipButton {... {
									tip: OptionUtils.getLabel('global.normal', 'config'),
									disable: loading,
									className: '-sb1 btn fa fa-cog'
								}} /> : null}
						</span>
					</>}
					{extCtrls && !loading ? <><i className={'-split'} />{extCtrls}</> : null}
				</div>
			</div>;
		};
		const _renderSub = (rowData, sub, idx) => {
			const {data, loading} = sub;
			if(data) return this.renderSubRow(rowData, data, idx);
			else{
				const {subApi} = this.props;
				if(!subApi) return <small className={'color-red'}>請補上subApi</small>;
				if(loading) return <Loading />;
				else if(_.isPlainObject(subApi) || _.isFunction(subApi)) {
					const {_criteria} = this.state;
					const {url, method, data:_data, query, onRequest, onSuccess} = _.isPlainObject(subApi) ? subApi : subApi(rowData, _criteria);
					this._ajax.renderSub = new _Ajax({
						url, method: method || 'get',
						data: _data ? _data : {
							... _criteria,
							... query && query(rowData)
						},
						onRequest: onRequest || (() => {
							sub.loading = true;
							this.forceUpdate();
						}),
						onSuccess: onSuccess || ((data, xhr) => {
							sub.loading = false;
							sub.data = data;
							this.forceUpdate();
						}),
					}).send();
				}
				else this._ajax.renderSub = new _Ajax({
					url: subApi + '/' + rowData.id,
					method: 'get',
					onRequest: () => {
						sub.loading = true;
						this.forceUpdate();
					},
					onSuccess: (data, xhr) => {
						sub.loading = false;
						sub.data = data;
						this.forceUpdate();
					},
				}).send();
			}
		};

		const _renderTHeader = () => {
			const {gridHeader:h} = this.props;
			return <thead>
				{typeof h == 'function' ? h(this.TH, this.state) : h || this.renderGridHeader()}
			</thead>;
		};
		const _renderTBody = () => {
			const {gridRow: row, gridTail: tail} = this.props;
			let keyMap = {};
			return <>
				{
					data ? data.map((d, idx) => <React.Fragment
						key={d.id && d.id !== -403 ? JSON.stringify(d.id) : idx}
					>
						{
							_.isObject(ipe) && _.isEqual(ipe, d) ?
								<Forming ref={ele => this._dom.ipeForm = ele} inputs={this._inputs}>
									<tr ref={ele => this._dom.ipeRow = ele} className={'-editing'}>{this.renderEditRow(d, idx, this._inputs)}</tr>
								</Forming> :
								_.isFunction(this.renderGridBodyByRow) ? this.renderGridBodyByRow(d, idx, data,keyMap) :<tr>{_.isFunction(row) ? row(d, idx, this.TD) : this.renderGridBody(d, idx, data,keyMap)}</tr>
						}
						{d._sub && d._sub.active ? <tr className={'-sub'}>
							<td><i /></td>
							<td colSpan={colWidths.length - 1}>{_renderSub(d, d._sub, idx)}</td>
						</tr> : null}
					</React.Fragment>) : null
				}
				{
					ipe === 'add' ? <Forming ref={ele => this._dom.ipeForm = ele} inputs={this._inputs}>
						<tr ref={ele => this._dom.ipeRow = ele} className={'-adding'}>
							{this.renderEditRow({}, data.length, this._inputs)}
						</tr>
					</Forming> : null
				}
				{data ? _.isFunction(tail) ? tail(data) : tail || this.renderGridFooter(data) : null}
			</>;
		};
		const _renderGrid = () => <table className={'-grid'}>
			<ColGroup cols={colWidths} />
			{_renderTHeader()}
			<tbody>
				{_renderTBody()}
			</tbody>
		</table>;
		const _renderFixRow = () => {
			return this.renderFixRow();
		};
		return <div {... {
			ref: ele => this._dom.root = ele,
			className: `H2-Grid ${className} ${theme ? theme : ''}${ipe ? ' ipe' : ''}${freezing ? ' freezing' : ''}`,
			style,
		}}>
			{ctrlPanel ? _renderCtrlPanel() : null}
			{
				error ? <div className={'-noticeWrapper error'}>
					<div className={'-msg msg red'}><i className={'fa fa-exclamation-triangle'} /> {error}</div>
				</div> :
				loading ? <div className={'-noticeWrapper'}><Loading size={'2x'}>loading...</Loading></div> :
				!data ? null :
				data.length === 0 && !fixData?.length && ipe != 'add' ? (emptyWarning ? <div className={'-noticeWrapper'}>
					<div className={'-msg msg blue'}><i className={'fa fa-info-circle'} /> {emptyWarning}</div>
				</div> : null) :
				freezing ? <Freezing {...{
					frozen: freezing,
					... freezingContainer ? {style: {
						... freezingContainer.width ? {maxWidth: freezingContainer.width} : null,
						... freezingContainer.height ? {maxHeight: freezingContainer.height} : null,
					}} : null
				}}>{_renderGrid()}</Freezing> : freezeHeader ? <>
					<table className={'-grid fixedHeader'}>
						<ColGroup cols={colWidths} />
						{_renderTHeader()}
					</table>
					<div {...{
						ref: ele => this._dom.gridWrapper = ele,
						className: '-gridWrapper',
						...rowsPerStepping > 0 && allData.length > steppedRows ? {onScroll: this._stepData} : null,
					}}>
						<table className={'-grid'}>
							<ColGroup cols={colWidths} />
							<tbody>
								{_renderFixRow()}
								{_renderTBody()}
							</tbody>
						</table>
					</div>
				</> : _renderGrid()
			}

			{newDlg ? this._NewDialog(newDlg) : null}
			{editDlg ? _genEditDialog(editDlg, editDlgProps) : null}
			{_.isNumber(browseDlg) || _.isNumber(browseDlg.idx) ? _genBrowseDialog(browseDlg) : null}
			{arrDlg ? this._ArrangeDialog : null}
			{extDialogs ? _.keys(extDialogs).map(key => this.state[key + 'Dlg'] ? _genExtDialog(key, this.state[key + 'Dlg']) : null) : null}

			{confirm && <Confirm {...{
				yes: () => _.isFunction(this.deleteHandler) ? this.deleteHandler(checkedData, this.state.data) : this._deleteAjax(confirm.ids).send(),
				close: () => this.setState({confirm: false}),
			}}>{confirm.msg}</Confirm>}
		</div>;
	}
}

const _clear = src => src === 0 ? true : _.isNil(src) || _.isNaN(src) || src === '' || (_.isArray(src) && (src.length === 0 || !src.some(_clear))) ? undefined : src;
class BasicCriteriaPanel extends HAComponent{
	static version = '1.1.0';
	state = {
		criteria: {},
		inputs: [],
		// ex: [
		//		input1, input2, {
		//			active: 'key1',
		//			inputs: {key1: input3_1, key2: input3_2},
		//			preValues: {key1: preValue, key2: preValue},
		//		}, input3, ....
		// ]
	};

	constructor(props) {
		super(props);
		const {src, onEditing, onUpdate, theme} = this.props;
		const {criteria, inputs} = this.state;
		_.forEach(src, (component, key) => {
			const _genInput = (c, key, prefix, orObject) => {
				const {label, input, fireOnEditing, componentOnUpdate} = c;
				const { type, props } = input || 0;
				const isPickerSeries = type && !['Select'].includes(props.component)
					? type.name.endsWith('Picker') || type.name.endsWith('DataLoader')
					: false;
				const isEditing = c.hasOwnProperty('fireOnEditing') ? fireOnEditing : isPickerSeries;
				const {inactiveIsReadOnly: iar, filter: f} = input.props;
				const inactiveIsReadOnly = iar !== undefined ? iar : false;
				const filter = f ? f : d => true;
				const _clone = React.cloneElement(input, {
					key: input.key || key,
					... input.props.placeholder ? null : {placeholder: label},
					//initValue: value,
					...isPickerSeries && { behavior: 'jira', inactiveIsReadOnly, filter },
					onUpdate: v => {
						this.state.criteria[key] = v;
						if (orObject) orObject.preValues[key] = v;
						this.forceUpdate(() => {
							if (_.isFunction(onUpdate)) onUpdate(this.getCriteria());
							if (_.isFunction(componentOnUpdate)) componentOnUpdate(v);
						});
					},
					... isEditing && {onEditing: v => {
						this.state.criteria[key] = v;
						this.forceUpdate(() => _.isFunction(onEditing) ? onEditing(this.getCriteria()) : null);
					}},
					...theme === 'portal'
						? {
							label,
							..._.isFunction(prefix) && { prefix: true, renderPrefix: prefix },
						} : _.isFunction(prefix)
							? { prefix: true, renderPrefix: prefix }
							: { tip: label },
				});
				return _clone;
			};
			if(key === '$OR'){
				const orIndex = inputs.length;
				const orKeys = _.keys(component);
				const activeKey = _.findKey(component, {active: 1}) || orKeys[0];
				const labels = _.map(component, 'label');
				const orObject = {
					props: { label: Object.values(component).map(m => m.text).join('') || labels.join('/') },
					active: activeKey,
					inputs: {},
					preValues: {},
				};
				inputs.push(orObject);
				_.forEach(orKeys, (orKey, idx) => {
					orObject.inputs[orKey] = _genInput(component[orKey], orKey, () => {
						return <span {... {
							className: `-prefix control`,
						}}>
							<Select {...{
								className: 'sm empty',
								data: _.map(orKeys, k => ({id: k, name: component[k].label})),
								initValue: this.state.inputs[orIndex].active,
								menuProps: {
									//eventLevel: this.getEventLevel() + 1,
									className: 'level2',
									stickDirection: 'top',
									stickOffset: {x: -5, y: -5},
									pointer: 'bottom',
									pointerClassName: 'sm',
								},
								onUpdate: v => {
									_.forEach(orKeys, k => this.state.criteria[k] = null);
									orObject.active = v;
									if (orObject.preValues[v] !== undefined)
										this.state.criteria[v] = orObject.preValues[v];
									else
										this.state.criteria[v] = orObject.inputs[v].props.value || orObject.inputs[v].props.initValue;
									this.forceUpdate(_.isFunction(onUpdate) ? () => onUpdate(this.getCriteria()) : null);
								},
							}} />
						</span>;
					}, orObject);
				});
				const activeInput = orObject.inputs[orObject.active];
				const value = activeInput.props.value || activeInput.props.initValue;
				criteria[orObject.active] = _.isNil(value) ? (activeInput.props.binary ? 0 : undefined) : value;
			}
			else {
				const input = _genInput(component, key);
				inputs.push(input);
				const value = input.props.value || input.props.initValue;
				criteria[key] = _.isNil(value) ? (input.props.binary ? 0 : undefined) : value;
			}
		});
	}

	componentDidMount() {
		super.componentDidMount();
		const {onMounted} = this.props;
		_.isFunction(onMounted) && onMounted(this.getCriteria());
	}

	getCriteria(){
		return _.pickBy(this.state.criteria, _clear);
	}

	render(){
		const {disable, theme} = this.props;
		const { inputs, criteria } = this.state;
		return <span className={'-basicCriteriaPanel'}>
			{_.map(inputs, (input, idx) => {
				const _genInput = (input) => {
					if(input.type && input.type.name) return React.cloneElement(input, {disable});
					else {
						const initValue = criteria[input.active];
						return React.cloneElement(input.inputs[input.active], {
							disable,
							...initValue && { initValue },
						});
					}
				};
				return theme === 'portal' ? <div className={'-criteria'} key={idx}>
					<label>{_.get(input, ['props', 'label'])}</label>
					{_genInput(input)}
				</div> : _genInput(input);
			})}
		</span>;
	}
}
class AdvCriteriaBox extends Dialog{
	static version = '1.0.5';
	static defaultProps = {
		... Dialog.defaultProps,
		closeByESC: true,
		closeByClickDocument: false,
		theme: 'gridAdv',
		height: undefined, // 由內容決定高度，非 Dialog 預設的 98%
		width: ['97%', 940, 1335],
		isHeader: false,
		stickDirection: 'bottom',
		stickOffset: {y: -4},
		modal: 'light',
		openFx: 'an-fadeInDown',
		isDraggable: false,
		pointer: 'top',
		pointerStyle: {left: 100},
		pointerClassName: 'sm',
		wrapperStyle: { maxHeight: '75vh', overflow: 'auto' },
	};
	state = {
		... this.state,
		criteria: {},
		descriptor: {},
		_inputs: {},
	};

	constructor(props) {
		super(props);
		const {src, criteria:initCriteria} = this.props;
		const {criteria, descriptor, _inputs} = this.state;
		_.forEach(src, ({label, input, inputs}, key) => {
			const isPickerSeries = input && input.type ? _.endsWith(input.type.name, 'Picker') || _.endsWith(input.type.name, 'DataLoader') : false;
			const _genInput = (c, _key, prefix, orObject) => {
				const {label, input} = c;
				const {inactiveIsReadOnly: iar, filter: f} = input.props;
				const inactiveIsReadOnly = iar !== undefined ? iar : false;
				const filter = f ? f : d => true;
				const _clone = React.cloneElement(input, {
					key: _key,
					ref: ele => this.inputs[_key] = ele,
					... input.props.placeholder ? null : {placeholder: label},
					... initCriteria.hasOwnProperty(_key) ? {initValue: initCriteria[_key]} : null,
					... isPickerSeries ? {inactiveIsReadOnly, filter, maxHeight: 76} : null,
					onUpdate: (v, v1, _this) => {
						criteria[_key] = v;
						if(orObject) orObject.preValues[_key] = v;
						if(_this && _this.getText)
							descriptor[_key] = {
								text: _this.getText(),
								... orObject ? {key} : null,
							};
					},
					... _.isFunction(prefix) ? {
						prefix: true, renderPrefix: prefix,
					} : {tip: label},
				});
				return _clone;
			};
			if(input){
				const _clone = _genInput({label, input}, key);
				_inputs[key] = _clone;
				const value = _clone.props.initValue ?? _clone.props.value;
				criteria[key] = _.isNil(value) ? (isPickerSeries && _clone.props.binary ? 0 : undefined) : value;
				descriptor[key] = {text: undefined};
			}
			else if(inputs){
				const orKeys = _.keys(inputs);
				let activeKey = null;
				_.forEach(orKeys, k => initCriteria.hasOwnProperty(k) ? activeKey = k : null);
				if(!activeKey) activeKey = _.findKey(inputs, {active: 1}) || orKeys[0];
				const orObject = {
					active: activeKey,
					inputs: {},
					preValues: {},
				};
				_inputs[key] = orObject;
				_.forEach(orKeys, (orKey, idx) => {
					orObject.inputs[orKey] = _genInput(inputs[orKey], orKey, () => {
						return <span {... {
							className: `-prefix control`,
						}}>
							<Select {...{
								className: 'sm empty',
								data: _.map(orKeys, k => ({id: k, name: inputs[k].label})),
								initValue: (this.state._inputs[key] || {}).active || activeKey,
								menuProps: {
									//eventLevel: this.getEventLevel() + 1,
									className: 'level2',
									stickDirection: 'top',
									stickOffset: {x: -5, y: -5},
									pointer: 'bottom',
									pointerClassName: 'sm',
								},
								onUpdate: v => {
								 	_.forEach(orKeys, k => {
								 		this.state.criteria[k] = null;
										this.state.descriptor[k] = null;
									});
								 	orObject.active = v;
								 	this.forceUpdate(() => {
										this.state.descriptor[v] = { key, text: this.inputs[v].getText() };
									});
								},
							}} />
						</span>;
					}, orObject);
				});
				const activeInput = orObject.inputs[orObject.active];
				const value = activeInput.props.initValue || activeInput.props.value;
				criteria[orObject.active] = _.isNil(value) ? (activeInput.props.binary ? 0 : undefined) : value;
				descriptor[orObject.active] = {text: undefined, key};
			}
		});
	}

	componentDidMount() {
		super.componentDidMount();
		const { descriptor: initDescriptor } = this.props;
		const { descriptor } = this.state;
		_.forEach(this.inputs, (input, key) => {
			const text = input.getText() || ((initDescriptor && initDescriptor[key]) || {}).text || input.getValue();
			if(text) descriptor[key].text = text;
		});
	}

	renderFooter(){
		const {close, onOk, src} = this.props;
		const isSubmittable = this.submittable();
		return <>
			<span {... {
				ref: ele => this._dom.submitBtn = ele,
				className: `btn btn-primary`,
				onClick: isSubmittable && (evt => setTimeout(() => {
					if(!this.validate()){
						const s = this._dom.submitBtn;
						s.addClass('errorShake').removeClass.delay(200, s, 'errorShake');
						return false;
					}
					else if(_.isFunction(onOk)){
						const values = _.pickBy(this.state.criteria, _clear);
						const descriptor = _.pickBy(this.state.descriptor, t => t && _clear(t.text));
						_.forEach(descriptor, (values, key) => {
							const input = (src[key] || src[values.key].inputs[key]).input;
							const isRangeSeries = _.endsWith(input.type.name, 'Range');
							const v = values.text;
							if(isRangeSeries) values.text = `${v[0] || ''} ~ ${v[1] || ''}`;
							else if(_.isArray(v)) values.text = _.join(v, ', ');
						});
						onOk(values, descriptor);
					}
				}, 0)),
			}}>{OptionUtils.getLabel('components.Grids', 'inquire')}</span>
			<span {... {
				className: 'btn btn-default',
				onClick: evt => close(),
			}}>{OptionUtils.getLabel('global.normal', 'cancel')}</span>
		</>;
	}

	renderBody(){
		const {src} = this.props;
		const {_inputs, criteria} = this.state;
		const _renderCombo = meta => {
			const preValue = meta.preValues[meta.active] || meta.inputs[meta.active].props.initValue;
			criteria[meta.active] = preValue;
			return React.cloneElement(meta.inputs[meta.active], {
				initValue: preValue,
			});
		};
		return _.map(src, ({label, width}, key) => {
			const input = _inputs[key];
			return <span {...{
				key,
				className: `-criterion${(width && _.reduce(width, (result, value, key) => {
					result += ` col${key}-${value}`;
					return result;
				}, '')) || ''}`,
			}}>
				<span className={'-cLabel'}>{label}</span>
				<span className={'-cInput'}>{input.type && input.type.name ? input : _renderCombo(input)}</span>
			</span>;
		});
	}
}
class AdvCriteriaDescriptor extends HAComponent{
	render(){
		const {src, criteria, descriptor, onClick, onRemove} = this.props;
		return <div {... {
			onClick,
			className: '-advCriteriaDescriptor',
		}}>
			<strong className={'text-lg color-blue'} style={{marginRight: 3}}>{_.size(criteria)}</strong>
			個條件:
			{_.map(_.keys(criteria), key => {
				if(!descriptor[key]) return;
				const s = src[descriptor[key].key || key];
				if(!s) return;

				const text = descriptor[key].text;
				return s.essential ? <span key={key} className={'-criteria essential'}>
					{s.label}: <strong className={'color-blue'}>{text}</strong>
					<i className={'fa fa-lock-alt'} />
				</span> : <TipBinder {...{
					key,
					className: '-criteria',
					direction: 'top',
					tip: text,
				}}>
					{s.label}
					<span {... {
						className: 'btn btn-xs square btn-danger fal fa-times',
						onClick: evt => {
							evt.stopPropagation();
							_.isFunction(onRemove) && onRemove(key);
						}
					}} />
				</TipBinder>;
			})}
		</div>;
	}
}

// TODO: old version, eliminate it soon!
class __BasicCriteriaPanel extends HAComponent{
	static version = '0.9.0';

	componentDidMount() {
		super.componentDidMount();

		const {inputs, fetch, _preCriteria, _criteria} = this.props;

		_.forEach(inputs, (input, id) => {
			_criteria[id] = _clear(input.getValue());
			input._onUpdate = input._onEditing = v => {
				_criteria[id] = _clear(v);
				if(_.isEqual(_preCriteria, _criteria)) return;
				fetch();
			};
		});
	}

	render(){
		return <span className={'-basicCriteriaPanel'}>{this.props.children}</span>;
	}
}
// TODO: old version, eliminate it soon!
class __AdvCriteriaPanel extends Dialog{
	static version = '1.0.0';
	static defaultProps = {
		... Dialog.defaultProps,
		theme: 'gridAdv',
		width: ['97%', 940, 1335],
		isHeader: false,
		stickDirection: 'bottom',
		bodyClassName: 'form-horizontal',
		modal: 'transparent',
		openFx: 'an-fadeInDown',
		isDraggable: false,
		pointer: 'top',
		pointerStyle: {left: 60},
		close: () => {},
	};

	componentDidMount() {
		super.componentDidMount();

		const {inputs, values} = this.props;
		_.values(inputs).map(input => {
			if(!input || !input.input) return;
			input.input.addValidateRule({required: false});
		});
		if(!values) return;
		_.keys(values).map(key => {
			if(!inputs[key] || !inputs[key].setValue) return;
			inputs[key].setValue(values[key]);
		});
	}
}

class SimpleGrid extends BasicGrid {
	static version = '0.0.1';
	static defaultProps = {
		... BasicGrid.defaultProps,
		isBasicCriteria: false,
		isConfigBtn: false,
		ctrlPanel: false,
		method: 'get',
		emptyWarning: OptionUtils.getLabel('global.normal', 'noInfo'),
	};
	static skipPrivileges = { query: () => true, edit: () => true, del: () => true };

	// TODO: 以下這段拿掉, 若需要以props.data更新, 與重新mount的方式取代
	static getDerivedStateFromProps(props, state) {
		const { data: propData } = props;
		const { initData } = state;
		const update = {};
		if (propData !== initData) {
			update.data = propData;
			update.initData = propData;
		}
		return _.isEmpty(update) ? null : update;
	}
}

class IPEGrid extends BasicGrid{
	static version = '0.0.1';
	static defaultProps = {
		... BasicGrid.defaultProps,
		api: PropTypes.string,
		ctrlPanel: false,
		method: 'get',
		emptyWarning: '暫無資料',
	};
	_operatingBehavior = 'ipe';

	add(){
		return this.setState({ipe: 'add'});
	}
}

class PageGrid extends BasicGrid {
	static defaultProps = {
		... BasicGrid.defaultProps,
		rowLimit: 1000,
	};

	state = {
		...this.state,
		page: 1,
	};

	TH = {
		...this.TH,
		sortable: props => {
			const { sortKey: key, advState: s, rowSpan } = props;
			const {advState} = this.props;
			const { sortKey: sk, sortOrder: so, criteriaType: t, advState: as, ipe, delIds } = this.state;
			const sortBy = key;
			const sortOrder = (sk == key && so === 'asc') ? 'desc' : 'asc';
			return <td {... {
				... ipe ? null : {className: `-sortable ${sk == key ? so : ''}`},
				...rowSpan ? { rowSpan } : null,
				...!ipe && {
					onClick: delIds ? null : () => {
						const { data } = this.state;
						this.setState({
							sortKey: key,
							sortBy,
							sortOrder,
							page: 1
							// ...this._sortData(data, sortBy, sortOrder),
						}, () => this.reload());
			   }}
			}}>{props.children}{s && t === 'adv' ? <span className={'-advState'}>{advState.items[as || advState.init]}</span> : null}</td>;
		},
		i18nSortable: props => {
			const { sortKey: key, rowSpan, showLang=true } = props;
			const { sortKey: sk, sortOrder: so, ipe, delIds } = this.state;
			const sortBy = `${key}.${user.locale}`;
			const sortOrder = (sk == key && so === 'asc') ? 'desc' : 'asc';
			return <td {... {
				... ipe ? null : {className: `-sortable ${sk == key ? so : ''}`},
				...rowSpan ? { rowSpan } : null,
				...!ipe && {
					onClick: delIds ? null : () => {
						const { data } = this.state;
						this.setState({
							sortKey: key,
							sortBy,
							sortOrder,
							page: 1
							// ...this._sortData(data, sortBy, sortOrder),
						}, () => this.reload());
				}}
			}}>{props.children} {showLang ? <span className={'-i18n'}>({Constants.Locales[user.locale]})</span> : null}</td>;
		}
	};

	fetch(){
		const {api, query, method, fetchErrMsg, fetchErrCode, onLoadSuccess, onError, rowsPerStepping } = this.props;
		const {criteriaType, _criteria, sortBy, sortOrder, page } = this.state;
		if(this._ajax.fetch && this._ajax.fetch.isRunning()) this._ajax.fetch.cancel();
		this._ajax.fetch = new _Ajax({
			url: api,
			method: method || 'post',
			data: _.pickBy(this._operatingBehavior === 'ipe' ? query : {
				$criteriaType: criteriaType,
				rowLimit: this.props.rowLimit,
				... _criteria,
				... query,
				sortBy,
				sortOrder,
				page,
			}, _clear),
			noticeContainer: this._dom.root,
			onRequest: () => this.setState({
				loading: true,
				data: null,
				checkedData: [],
				error: false,
				steppedRows: rowsPerStepping,
			}, () => {
				const MapComponent = this.props.fetchComponent;
				if (MapComponent) MapComponent.fetch(MapComponent.defaultProps);
			}),
			onSuccess: ({data=[], total=0, exportIds =[]}, xhr) => {
                const checkableMap = this._getCheckableMap();
                const preCheckedIdMap = _.keyBy(this.state.preCheckedIds);
                const preCheckedIds = [];
                const checkedData = [];
                data.forEach(d => {
                    d._checked = !!preCheckedIdMap[d.id] && _.some(checkableMap, (fn) => fn === false ? false : fn === true || fn(d, this));
                    if (d._checked) {
                        preCheckedIds.push(d.id);
                        checkedData.push(d);
                    }
                });
				this.setState({
					loading: false,
					data,
					renderData: data,
					total,
					exportIds,
					... criteriaType === 'basic' ?
						{_preCriteria: _.cloneDeep(_criteria)} :
						{_preAdvCriteria: _.cloneDeep(_criteria)},
					preCheckedIds,
					checkedData,
					error: false
				}, () => _.isFunction(onLoadSuccess) ? onLoadSuccess(data) : null);
			},
			errMsg: fetchErrMsg,
			errCode: fetchErrCode,
			onError: (xhr) => {
				this.setState({
					loading: false,
					data: null,
					total: 0,
					error: true,
					exportIds: [],
				}, () => _.isFunction(onError) ? onError() : null);
			}
		}).send();
	}

	render(){
		const {
			theme, style,
			caption, ctrlPanel, extCtrls, isBasicCriteria:isBasic, isAdvCriteria:isAdv,
			api, isInfo, isRefreshBtn, isConfigBtn, isExportBtn,
			colWidths, fetch,
			extDialogs, emptyWarning,
			freezeHeader, freezing, freezingContainer,
			rowsPerStepping,
			className, rowLimit,
		} = this.props;
		const {
			criteriaType, loading, error, openAdvPanel,
			newDlg, editDlg, editDlgProps, browseDlg, arrDlg, ipe, confirm,
			data: allData, renderData: data, checkedData,
			steppedRows, fixData, total, page, delIds,
		} = this.state;
		// page max
		const maxPage = total ? Math.ceil(total/rowLimit) : 1;

		const _genEditDialog = (data, props) => {
			const {editDialog:dialog} = this.props;
			const onSuccess = dialog && dialog.props.onSuccess;
			return dialog ? React.cloneElement(dialog, {
				[fetch === false ? 'data' : 'dataFromGrid']: data,
				id: data.id,
				onSuccess: _.isFunction(onSuccess) ? onSuccess : (data, xhr) => this.reload(),
				close: () => this.setState({editDlg: false}),
				parent: this,
				... props,
			}) : null;
		};
		const _genBrowseDialog = browseDlg => {
			if(!allData) return null;
			const {browseDialog: dialog} = this.props;
			const { userPrivileges: { edit: pEdit } } = this.state;
			const idx = _.isNumber(browseDlg) ? browseDlg : browseDlg.idx;
			// !!! editable 非 function 定無法隨上下筆變動
			const editable = !!pEdit && browseDlg.editable !== false && (_data => _data.$edit !== false &&
				(typeof browseDlg.editable !== 'function' || browseDlg.editable(_data)));
			return dialog ? React.cloneElement(dialog, {
				idx, editable,
				dataSet: allData.map(d => ({id: d.id, [fetch === false ? 'data' : 'dataFromGrid']: d})),
				edit: (i, d) => this.setState({editDlg: d, editDlgProps: dialog.props.editProps, browseDlg: false}),
				close: () => this.setState({browseDlg: false}),
				parent: this,
			}) : null;
		};
		const _genExtDialog = (key, data) => {
			const dialog = extDialogs[key];
			return React.cloneElement(dialog, {
				key: key,
				...fetch === false ? {data} : {dataFromGrid: data},
				id: data.id,
				onSuccess: () => this.reload(),
				close: () => this.setState({[key + 'Dlg']: false}),
				parent: this,
			});
		};

		const _renderCtrlPanel = () => {
			const {basicCriteria, advCriteria, theme} = this.props;
			const {queryOnEditing, _criteria, _preCriteria, _preAdvCriteria, _criteriaDescriptor} = this.state;
			const _renderCriteriaBtn = () => {
				return (isBasic || isAdv) || (basicCriteria || advCriteria) ? <>
					<span className={'btn-group btn-group-white'} style={{margin:'3px 0'}}>
						{isBasic || basicCriteria ? <TipButton {... {
							tip: OptionUtils.getLabel('components.grids', 'simpleSearch'),
							className: `-sb btn fa fa-search ${criteriaType === 'basic' && !openAdvPanel ? ' active' : ''}`,
							onClick: evt => {
								if (criteriaType == 'basic' || delIds) return;
								this._criteria = {};
								this._preCriteria = {};
								this._preAdvCriteria = {};
								this.setState({
									criteriaType: 'basic',
									advState: null,
									_criteria: this.state._preCriteria,
									page: 1,
								}, this.reload);
							}
						}} /> : null}
						{isAdv || advCriteria ? <TipButton {... {
							tip: OptionUtils.getLabel('components.grids', 'advancedSearch'),
							className: `-sb btn fa fa-sliders-h ${criteriaType === 'adv' || openAdvPanel ? 'active' : ''}`,
							onClick: evt => {
								if (criteriaType == 'adv' || delIds) return;
								if(_preAdvCriteria)
									this.setState({criteriaType: 'adv', openAdvPanel: false, _criteria: _preAdvCriteria, page: 1}, this.reload);
								else this.setState({criteriaType: 'adv', openAdvPanel: true});
							},
						}} /> : null}
					</span>
					{criteriaType ? <small className={'fa fa-chevron-right color-lightGray'} style={{margin:'0 4px 0 6px'}} /> : null}
				</> : null;
			};
			const _renderAdvStateBtn = () => {
				if(!this.props.advState) return;
				const {advState:{init, items}} = this.props;
				const {advState} = this.state;
				const v = advState ? advState : init;
				if(!advState) this.state.advState = v;
				return <>
					<span className={'-advStateBtnGroup btn-group btn-group-white'}>
						{_.map(items, (value, key) => <span {... {
							key: key,
							className: `btn green ${v == key ? ' active' : ''}`,
							onClick: evt => {
								evt.stopPropagation();
								this.setState({advState: key});
							}
						}} >{value}</span>)}
					</span>
					<i className={'-split'} />
				</>;
			};
			const _nowPage = () =>  this.state.page;

			return <div className={'-ctrlPanel'} ref={ele => this._dom.ctrlPanel = ele}>
				{checkedData.length > 0 && <div className={'-manipulator'}>
					{this.renderManipulator(checkedData)}
				</div>}
				{caption ? <span className={'-caption'}>{caption}</span> : null}
				{this.state.userPrivileges.query && <>
					{(isBasic || basicCriteria) && (!isAdv && !advCriteria) ? <i className={'-basicIcon fal fa-search'} /> : _renderCriteriaBtn()}
					{isBasic && criteriaType === 'basic' && (
						this.renderBasicCriteria ? <__BasicCriteriaPanel {... {	// TODO: old version, eliminate it soon!
								inputs: this.basicCriteria,
								fetch: this.__fetchData,
								_preCriteria: this._preCriteria,
								_criteria: this._criteria,
							}}>{this.renderBasicCriteria()}</__BasicCriteriaPanel> :
							<BasicCriteriaPanel {... {
								src: basicCriteria,
								onMounted: criteria => this.state._criteria = criteria,
								onEditing: criteria => {
									this.state._criteria = criteria;
									this.state.queryOnEditing = true;
									this.refresh();
								},
								onUpdate: criteria => {
									this.state._criteria = criteria;
									this.state.queryOnEditing = false;
									this.state.page = 1;
									this.refresh();
								},
								disable: (!queryOnEditing && loading) || delIds,
								theme,
							}} />
					)}
					{criteriaType === 'adv' && openAdvPanel && <small className={'color-lightGray'}>{OptionUtils.getLabel('components.grids', 'advancedSearchConfig')}...</small>}
					{criteriaType === 'adv' && !openAdvPanel && (this.renderAdvCriteria ? <span {... {	// TODO: old version, eliminate it soon!
						className: '-advCriteria',
						... loading ? null : {onClick: evt => this.setState({openAdvPanel: true})},
					}}>
					<span className={loading ? '' : 'link'}>
						{/* {_.size(this._preAdvTips)} 個條件 */}
						<I18n key1='components.grids' key2='counditionCount' options={{
							count: _.size(this._preAdvTips)
						}} />
						{this._preAdvTips && this._preAdvTips.length > 0 ? `: (${this._preAdvTips})` : null}
					</span>
				</span> : <AdvCriteriaDescriptor {...{
						src: advCriteria,
						criteria: _.pickBy(_criteria, c => !_.isNil(c)),
						descriptor: _criteriaDescriptor,
							onClick: delIds ? null : () => this.setState({ openAdvPanel: true }),
							onRemove: delIds ? null : key => {
							_criteria[key] = undefined;
							if(_.size(_criteria) > 0) this.setState({_criteria}, this.reload);
							else this.setState({criteriaType: 'basic', _criteria: _preCriteria, _preAdvCriteria: null, page: 1}, this.reload);
						},
					}} />)}

					{
						openAdvPanel ? (
							this.renderAdvCriteria ? <__AdvCriteriaPanel {...{	// TODO: old version, eliminate it soon!
									inputs: this.advCriteria,
									values: this._preAdvCriteria,
									stick: this._dom.ctrlPanel,
									onOk: this.__loadByAdvCriteria.bind(this),
									onCancel: () => {
										if (!_.isEmpty(this._preAdvCriteria)) this.setState({ openAdvPanel: false });
										else this.setState({
											criteriaType: 'basic',
											openAdvPanel: false
										}, () => this.refresh());
									}
								}}>{this.renderAdvCriteria()}</__AdvCriteriaPanel> :
								<AdvCriteriaBox {... {
									stick: this._dom.ctrlPanel,
									src: advCriteria,
									criteria: _criteria,
									descriptor: _criteriaDescriptor,
									onOk: (criteria, descriptor) => this.setState({
										openAdvPanel: false,
										_criteria: criteria,
										_criteriaDescriptor: descriptor,
										page: 1,
									}, this.refresh),
									close: () => {
										if(_preAdvCriteria) this.setState({openAdvPanel: false});
										else this.setState({openAdvPanel: false, criteriaType: 'basic', _criteria: _preCriteria}, this.refresh);
									},
								}} />
						) : null
					}
				</>}
				<div className={'-right'}>
					{criteriaType === 'adv' && !openAdvPanel && _renderAdvStateBtn()}
					{isInfo && <>
						{!loading && allData && theme != 'portal' && <>
							{page > 1 && !delIds && <><span
								className="btn -sb1 btn-ico btn-white fas fa-arrow-alt-to-left"
								onClick={() => this.setState({ page: page - 1 }, () => this.reload())} />&nbsp;</>}
							<small className={''}>
								<RNumber {...{
									style: {height: 24, 'font-size': '12px', 'line-height': '17px'},
									width: (34 + ((maxPage.toString().length-1)*4)) * 2,
									value: page,
									initValue: page,
									validate: {
										required: true,
										max: maxPage,
										min: 1,
									},
									onUpdate: page => this.setState({page}, () => this.reload()),
									suffix: {
										className: 'text-center',
										text: `/ ${maxPage}`,
										width: 30 + ((maxPage.toString().length-1)*4),
									},
									disable: delIds,
								}}/>
							</small>&nbsp;
							{page < maxPage && !delIds && <><span
								className="btn -sb1 btn-ico btn-white fas fa-arrow-alt-to-right"
								onClick={() => this.setState({ page: page + 1 }, () => this.reload())} />&nbsp;</>}
							<small className={'msg sm gray50'}>
								<I18n key1='components.grids' key2='allCount' options={{'count': <strong className={'color-blue'}>{total ? total : 0}</strong>}} />
							</small>
						</>}
						{api && isRefreshBtn && !loading && <TipButton {... {
							tip: OptionUtils.getLabel('global.normal', 'refresh'),
							disabled: loading || delIds,
							className: '-refresh -sb1 btn btn-ico btn-white fa fa-redo',
							style: {marginLeft: 5},
							onClick: this.reload,
						}} />}
					</>}
					{(isExportBtn || isConfigBtn) && !loading && <>
						<i className={'-split'} />
						<span className={'btn-group btn-group-white'} style={{margin:0}}>
							{isExportBtn ? <TipButton {... {
								tip: OptionUtils.getLabel('global.normal', 'exportInfo'),
								disable: loading,
								className: '-sb1 btn fa fa-file-download'
							}} /> : null}
							{isConfigBtn ? <TipButton {... {
									tip: OptionUtils.getLabel('global.normal', 'config'),
									disable: loading,
									className: '-sb1 btn fa fa-cog'
								}} /> : null}
						</span>
					</>}
					{extCtrls && !loading ? <><i className={'-split'} />{extCtrls}</> : null}
				</div>
			</div>;
		};
		const _renderSub = (rowData, sub, idx) => {
			const {data, loading} = sub;
			if(data) return this.renderSubRow(rowData, data, idx);
			else{
				const {subApi} = this.props;
				if(!subApi) return <small className={'color-red'}>請補上subApi</small>;
				if(loading) return <Loading />;
				else if(_.isPlainObject(subApi) || _.isFunction(subApi)) {
					const {_criteria} = this.state;
					const {url, method, data:_data, query, onRequest, onSuccess} = _.isPlainObject(subApi) ? subApi : subApi(rowData, _criteria);
					this._ajax.renderSub = new _Ajax({
						url, method: method || 'get',
						data: _data ? _data : {
							... _criteria,
							... query && query(rowData)
						},
						onRequest: onRequest || (() => {
							sub.loading = true;
							this.forceUpdate();
						}),
						onSuccess: onSuccess || ((data, xhr) => {
							sub.loading = false;
							sub.data = data;
							this.forceUpdate();
						}),
					}).send();
				}
				else this._ajax.renderSub = new _Ajax({
					url: subApi + '/' + rowData.id,
					method: 'get',
					onRequest: () => {
						sub.loading = true;
						this.forceUpdate();
					},
					onSuccess: (data, xhr) => {
						sub.loading = false;
						sub.data = data;
						this.forceUpdate();
					},
				}).send();
			}
		};

		const _renderTHeader = () => {
			const {gridHeader:h} = this.props;
			return <thead>
				{_.isFunction(h) ? h(this.TH) : h ? h : this.renderGridHeader()}
			</thead>;
		};
		const _renderTBody = () => {
			const {gridRow: row, gridTail: tail} = this.props;
			let keyMap = {};
			return <>
				{
					data ? data.map((d, idx) => <React.Fragment key={d.id ? JSON.stringify(d.id) : idx}>
						{
							_.isObject(ipe) && _.isEqual(ipe, d) ?
								<Forming ref={ele => this._dom.ipeForm = ele} inputs={this._inputs}>
									<tr ref={ele => this._dom.ipeRow = ele} className={'-editing'}>{this.renderEditRow(d, idx, this._inputs)}</tr>
								</Forming> :
								_.isFunction(this.renderGridBodyByRow) ? this.renderGridBodyByRow(d, idx, data,keyMap) :<tr>{_.isFunction(row) ? row(d, idx, this.TD) : this.renderGridBody(d, idx, data,keyMap)}</tr>
						}
						{d._sub && d._sub.active ? <tr className={'-sub'}>
							<td><i /></td>
							<td colSpan={colWidths.length - 1}>{_renderSub(d, d._sub, idx)}</td>
						</tr> : null}
					</React.Fragment>) : null
				}
				{
					ipe === 'add' ? <Forming ref={ele => this._dom.ipeForm = ele} inputs={this._inputs}>
						<tr ref={ele => this._dom.ipeRow = ele} className={'-adding'}>
							{this.renderEditRow({}, data.length, this._inputs)}
						</tr>
					</Forming> : null
				}
				{data ? _.isFunction(tail) ? tail(data) : tail || this.renderGridFooter(data) : null}
			</>;
		};
		const _renderGrid = () => <table className={'-grid'}>
			<ColGroup cols={colWidths} />
			{_renderTHeader()}
			<tbody>
				{_renderTBody()}
			</tbody>
		</table>;
		const _renderFixRow = () => {
			return this.renderFixRow();
		};
		return <div {... {
			ref: ele => this._dom.root = ele,
			className: `H2-Grid ${className} ${theme ? theme : ''}${ipe ? ' ipe' : ''}${freezing ? ' freezing' : ''}`,
			style,
		}}>
			{ctrlPanel ? _renderCtrlPanel() : null}
			{
				error ? <div className={'-noticeWrapper error'}>
					<div className={'-msg msg red'}><i className={'fa fa-exclamation-triangle'} /> {error}</div>
				</div> :
				loading ? <div className={'-noticeWrapper'}><Loading size={'2x'}>loading...</Loading></div> :
				!data ? null :
				data.length === 0 && !fixData?.length && ipe != 'add' ? (emptyWarning ? <div className={'-noticeWrapper'}>
					<div className={'-msg msg blue'}><i className={'fa fa-info-circle'} /> {emptyWarning}</div>
				</div> : null) :
				freezing ? <Freezing {...{
					frozen: freezing,
					... freezingContainer ? {style: {
						... freezingContainer.width ? {maxWidth: freezingContainer.width} : null,
						... freezingContainer.height ? {maxHeight: freezingContainer.height} : null,
					}} : null
				}}>{_renderGrid()}</Freezing> : freezeHeader ? <>
					<table className={'-grid fixedHeader'}>
						<ColGroup cols={colWidths} />
						{_renderTHeader()}
					</table>
					<div {...{
						ref: ele => this._dom.gridWrapper = ele,
						className: '-gridWrapper',
						...rowsPerStepping > 0 && allData.length > steppedRows ? {onScroll: this._stepData} : null,
					}}>
						<table className={'-grid'}>
							<ColGroup cols={colWidths} />
							<tbody>
								{_renderFixRow()}
								{_renderTBody()}
							</tbody>
						</table>
					</div>
				</> : _renderGrid()
			}

			{newDlg ? this._NewDialog(newDlg) : null}
			{editDlg ? _genEditDialog(editDlg, editDlgProps) : null}
			{_.isNumber(browseDlg) || _.isNumber(browseDlg.idx) ? _genBrowseDialog(browseDlg) : null}
			{arrDlg ? this._ArrangeDialog : null}
			{extDialogs ? _.keys(extDialogs).map(key => this.state[key + 'Dlg'] ? _genExtDialog(key, this.state[key + 'Dlg']) : null) : null}

			{confirm && <Confirm {...{
				yes: () => _.isFunction(this.deleteHandler) ? this.deleteHandler(checkedData, this.state.data) : this._deleteAjax(confirm.ids).send(),
				close: () => this.setState({confirm: false}),
			}}>{confirm.msg}</Confirm>}
		</div>;
	}

}

class SearchContainer extends BasicGrid {
	// _fixContentHeight() {
	// 	// 由於沒有 wrapper 元素，需要直接去掉除 ctrlPanel 的高度(包含 margin)
	// 	const parent = this.props.container || this._dom.root.getParent();
	// 	const cRect = this._dom.ctrlPanel.getComputedSize();
	// 	const cCoordinates = this._dom.ctrlPanel.getCoordinates(parent);
	// 	console.log(cRect, cCoordinates);
	// 	const height = `calc(100% - ${cRect.totalHeight + cCoordinates.top}px)`;
	// 	this.setState({ gridHeight: height });
	// }

	render() {
		const {
			theme, style,
			caption, extCtrls, isBasicCriteria:isBasic, isAdvCriteria:isAdv,
			api, isInfo, isRefreshBtn, isConfigBtn, isExportBtn,
			className, filteredData, emptyWarning
		} = this.props;

		const {
			criteriaType, loading, openAdvPanel, ipe,
			data: allData, renderData: data, checkedData, delIds, error
		} = this.state;

		const _renderCtrlPanel = () => {
			const {basicCriteria, advCriteria, theme} = this.props;
			const {queryOnEditing, _criteria, _preCriteria, _preAdvCriteria, _criteriaDescriptor} = this.state;
			const _renderCriteriaBtn = () => {
				return (isBasic || isAdv) || (basicCriteria || advCriteria) ? <>
					<span className={'btn-group btn-group-white'} style={{margin:'3px 0'}}>
						{isBasic || basicCriteria ? <TipButton {... {
							tip: OptionUtils.getLabel('components.grids', 'simpleSearch'),
							className: `-sb btn fa fa-search ${criteriaType === 'basic' && !openAdvPanel ? ' active' : ''}`,
							onClick: evt => {
								if (criteriaType == 'basic' || delIds) return;
								this._criteria = {};
								this._preCriteria = {};
								this._preAdvCriteria = {};
								this.setState({
									criteriaType: 'basic',
									advState: null,
									_criteria: this.state._preCriteria,
									page: 1,
								}, this.reload);
							}
						}} /> : null}
						{isAdv || advCriteria ? <TipButton {... {
							tip: OptionUtils.getLabel('components.grids', 'advancedSearch'),
							className: `-sb btn fa fa-sliders-h ${criteriaType === 'adv' || openAdvPanel ? 'active' : ''}`,
							onClick: evt => {
								if (criteriaType == 'adv' || delIds) return;
								if(_preAdvCriteria)
									this.setState({criteriaType: 'adv', openAdvPanel: false, _criteria: _preAdvCriteria, page: 1}, this.reload);
								else this.setState({criteriaType: 'adv', openAdvPanel: true});
							},
						}} /> : null}
					</span>
					{criteriaType ? <small className={'fa fa-chevron-right color-lightGray'} style={{margin:'0 4px 0 6px'}} /> : null}
				</> : null;
			};
			const _renderAdvStateBtn = () => {
				if(!this.props.advState) return;
				const {advState:{init, items}} = this.props;
				const {advState} = this.state;
				const v = advState ? advState : init;
				if(!advState) this.state.advState = v;
				return <>
					<span className={'-advStateBtnGroup btn-group btn-group-white'}>
						{_.map(items, (value, key) => <span {... {
							key: key,
							className: `btn green ${v == key ? ' active' : ''}`,
							onClick: evt => {
								evt.stopPropagation();
								this.setState({advState: key});
							}
						}} >{value}</span>)}
					</span>
					<i className={'-split'} />
				</>;
			};

			return <div className={'-ctrlPanel'} ref={ele => this._dom.ctrlPanel = ele}>
				{checkedData.length > 0 && <div className={'-manipulator'}>
					{this.renderManipulator(checkedData)}
				</div>}
				{caption ? <span className={'-caption'}>{caption}</span> : null}
				{this.state.userPrivileges.query && <>
					{(isBasic || basicCriteria) && (!isAdv && !advCriteria) ? <i className={'-basicIcon fal fa-search'} /> : _renderCriteriaBtn()}
					{isBasic && criteriaType === 'basic' && (
						<BasicCriteriaPanel {... {
							src: basicCriteria,
							onMounted: criteria => this.state._criteria = criteria,
							onEditing: criteria => {
								this.state._criteria = criteria;
								this.state.queryOnEditing = true;
								this.refresh();
							},
							onUpdate: criteria => {
								this.state._criteria = criteria;
								this.state.queryOnEditing = false;
								this.refresh();
							},
							disable: (!queryOnEditing && loading) || delIds,
							theme,
						}} />
					)}
					{criteriaType === 'adv' && openAdvPanel && <small className={'color-lightGray'}>{OptionUtils.getLabel('components.grids', 'advancedSearchConfig')}...</small>}
					{criteriaType === 'adv' && !openAdvPanel && (<AdvCriteriaDescriptor {...{
						src: advCriteria,
						criteria: _.pickBy(_criteria, c => !_.isNil(c)),
						descriptor: _criteriaDescriptor,
							onClick: delIds ? null : () => this.setState({ openAdvPanel: true }),
							onRemove: delIds ? null : key => {
							_criteria[key] = undefined;
							if(_.size(_criteria) > 0) this.setState({_criteria}, this.reload);
							else this.setState({criteriaType: 'basic', _criteria: _preCriteria, _preAdvCriteria: null}, this.reload);
						},
					}} />)}
					{
						openAdvPanel ? (
							<AdvCriteriaBox {... {
								stick: this._dom.ctrlPanel,
								src: advCriteria,
								criteria: _criteria,
								descriptor: _criteriaDescriptor,
								onOk: (criteria, descriptor) => this.setState({
									openAdvPanel: false,
									_criteria: criteria,
									_criteriaDescriptor: descriptor,
								}, this.refresh),
								close: () => {
									if(_preAdvCriteria) this.setState({openAdvPanel: false});
									else this.setState({openAdvPanel: false, criteriaType: 'basic', _criteria: _preCriteria}, this.refresh);
								},
							}} />
						) : null
					}
				</>}
				<div className={'-right'}>
					{criteriaType === 'adv' && !openAdvPanel && _renderAdvStateBtn()}
					{isInfo && <>
						{!loading && allData && theme != 'portal' && <small className={'msg sm gray50'}>
							<I18n key1='components.grids' key2={filteredData ? filteredData.length > 1 ? 'allCounts': 'allCount' : allData.length > 1 ? 'allCounts': 'allCount'} options={{'count': <strong className={'color-blue'}>{filteredData ? filteredData.length : allData.length}</strong>}} />
						</small>}
						{!loading && allData && theme === 'portal' && <>
							{extCtrls && !loading ? <>
								{extCtrls}
								<span style={{ marginRight: 8 }}></span>
							</> : null}
							<small>
								<I18n key1='components.grids' key2={allData.length > 1 ? 'matchFewCounts' : 'matchFewCount'} options={{'count': <strong className={'color-blue'}>{allData.length}</strong>}} />
							</small>
						</>}
						{api && isRefreshBtn && !loading && <TipButton {... {
							tip: OptionUtils.getLabel('global.normal', 'refresh'),
							disabled: loading || delIds,
							className: '-refresh -sb1 btn btn-ico btn-white fa fa-redo',
							style: {marginLeft: 5},
							onClick: this.reload,
						}} />}
					</>}
					{(isExportBtn || isConfigBtn) && !loading && <>
						<i className={'-split'} />
						<span className={'btn-group btn-group-white'} style={{margin:0}}>
							{isExportBtn ? <TipButton {... {
								tip: OptionUtils.getLabel('global.normal', 'exportInfo'),
								disable: loading,
								className: '-sb1 btn fa fa-file-download'
							}} /> : null}
							{isConfigBtn ? <TipButton {... {
									tip: OptionUtils.getLabel('global.normal', 'config'),
									disable: loading,
									className: '-sb1 btn fa fa-cog'
								}} /> : null}
						</span>
					</>}
					{theme !== 'portal' && extCtrls && !loading ? <><i className={'-split'} />{extCtrls}</> : null}
				</div>
			</div>;
		};

		const _renderChildren = () => {
			if (error) {
				return <div className={'-noticeWrapper error'}>
					<div className={'-msg msg red'}><i className={'fa fa-exclamation-triangle'} /> {error}</div>
				</div>;
			}
			if (loading) {
				return <div className={'-noticeWrapper'}><Loading size={'2x'}>loading...</Loading></div>;
			}
			if (data?.length === 0 && ipe != 'add') {
				if (emptyWarning) {
					return <div className={'-noticeWrapper'}>
						<div className={'-msg msg blue'}><i className={'fa fa-info-circle'} /> {emptyWarning}</div>
					</div>;
				} else {
					return null;
				}
			}

			return _.isFunction(this.props.children) ? this.props.children(data, this) : this.props.children;
		};

		return <div {... {
			ref: ele => this._dom.root = ele,
			className: `H2-Grid${className ? ` ${className}` : ''}${theme ? ` ${theme}` : ''}${ipe ? ' ipe' : ''}`,
			style,
		}}>
			{_renderCtrlPanel()}
			{_renderChildren()}
		</div>;
	}
}

export { BasicGrid, SimpleGrid, IPEGrid, PageGrid, SearchContainer, AdvCriteriaBox, AdvCriteriaDescriptor, Manipulators, BasicCriteriaPanel };
